import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import sidebarApiClient from "@lift-admin/utils/sidebarApiClient";
import { generateUUID } from "@lift-admin/utils/uuid";
// import { ItemUpsert, Item } from "@lift-admin/common/apiTypes/sidebar";

// Temporary type until the sidebar types are properly imported
interface ItemUpsert {
  channel_id?: string;
  grouping_id?: string | null;
  display_name: string;
  icon?: string | null;
  route_endpoint: string;
  ui_component_id?: string;
  is_grouped: boolean;
  is_active: boolean;
  report_config?: string[];
  custom_component_config?: any;
  params?: any;
  report_type: string;
  level_access?: string;
  level_id?: any[];
  is_team_access?: boolean;
  is_team?: boolean;
  is_restricted?: boolean;
  close_sidebar_on_navigate?: boolean | null;
  sandbox_id?: string | null;
  brands_exclude?: any[];
  organizations_exclude?: any[];
  display_order?: number;
  below_item?: string | null;
}
// import 'react-data-grid/lib/styles.css';
import { Toastify } from 'lift-ui';
import {
  Button,
  DataTable,
  Badge,
  SpinnerLoader,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  Input,
  Label,
  Switch,
  Separator,
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
  MultiSelectCheckboxTag,
  SingleSelect,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "lift-ui";
import type { TableColumn } from "lift-ui";
import DynamicNestedForm from "./params-input";
import IconSelect from "./icon-select";
import { Info, Edit, Trash2, ChevronDown } from "lucide-react";
import BrandMultiSelect from "./brand-multiselect";
import OrganizationMultiSelect from "./organization-multiselect";
import { actionLog } from "../../../App";
import {
  buildDetails,
  buildSummary,
  diffFields,
  type FieldSpec,
} from "@lift-admin/utils/logChangeFormatter";
import {
  buildAccessListChange,
  resolveAccessEntryLabel,
} from "@/features/drawer-structure-v2/drawer-parts/accessLogHelpers";
import apiClientDrawerV2 from "@lift-admin/utils/apiClientDrawerV2";
import apiClientBrand from "@lift-admin/utils/apiClient";
import iconOptions from "../../../constants/iconOptions.json";

interface ItemsProps {
  toggle: string;
  channels: any[];
  groupings: any[];
  component: any[];
  customComponents: any[];
  drawerItemsDynamic: any[];
  reports: any[];
  reportsSandbox: any[];
  openDialog: boolean;
  setOpenDialog: (value: boolean) => void;
  brands: any[];
  organizations: any[];
  searchText: string;
  applications: any[];
  hasEditAccess?: boolean;
  hasManageAccess?: boolean;
}

interface FormStateEdit {
  id: string | null;
  display_name: string | null;
  route_endpoint: string | null;
  icon: string | null;
  selectedApplication: any | null;
  selectedChannel: any | null;
  selectedGroupings: any | null;
  selectedUIComponents: any | null;
  selectedCustomComponents: any[];
  selectedReports: any[];
  levelAccess: string;
  selectedBrands: any[];
  selectedOrganizations: any[];
  params: any;
  selectedLevelId: any[];
  report_type: string;
  isTeam: boolean;
  isRestricted: boolean;
  closeSidebarOnNavigate: boolean;
  selectedSandbox: any | null;
  brands_exclude: any[];
  organizations_exclude: any[];
  is_active: boolean;
  report_config: any;
  custom_component_config: any;
}

interface FormState {
  display_name: string | null;
  route_endpoint: string | null;
  icon: string | null;
  selectedApplication: any | null;
  selectedChannel: any | null;
  selectedGroupings: any | null;
  selectedUIComponents: any | null;
  selectedCustomComponents: any[];
  selectedReports: any[];
  levelAccess: string;
  selectedBrands: any[];
  selectedOrganizations: any[];
  params: any;
  selectedLevelId: any[];
  report_type: string;
  isTeam: boolean;
  isRestricted: boolean;
  closeSidebarOnNavigate: boolean;
  selectedSandbox: any | null;
  brands_exclude: any[];
  organizations_exclude: any[];
  is_active: boolean;
  report_config: any;
  custom_component_config: any;
}

const initialFormStateEdit: FormStateEdit = {
  id: null,
  display_name: null,
  route_endpoint: null,
  icon: null,
  selectedApplication: null,
  selectedChannel: null,
  selectedGroupings: null,
  selectedUIComponents: null,
  selectedCustomComponents: [],
  selectedReports: [],
  levelAccess: "All",
  selectedBrands: [],
  selectedOrganizations: [],
  params: [],
  selectedLevelId: [],
  report_type: "Default",
  isTeam: false,
  isRestricted: false,
  closeSidebarOnNavigate: false,
  selectedSandbox: null,
  brands_exclude: [],
  organizations_exclude: [],
  is_active: true,
  report_config: null,
  custom_component_config: null,
};

const initialFormState: FormState = {
  display_name: null,
  route_endpoint: null,
  icon: null,
  selectedApplication: null,
  selectedChannel: null,
  selectedGroupings: null,
  selectedUIComponents: null,
  selectedCustomComponents: [],
  selectedReports: [],
  levelAccess: "All",
  selectedBrands: [],
  selectedOrganizations: [],
  params: [],
  selectedLevelId: [],
  report_type: "Default",
  isTeam: false,
  isRestricted: false,
  closeSidebarOnNavigate: false,
  selectedSandbox: null,
  brands_exclude: [],
  organizations_exclude: [],
  is_active: true,
  report_config: null,
  custom_component_config: null,
};

const BrandCellRenderer = ({ data, brandsList }: { data: any, brandsList: any[] }) => {
  if (
    !data ||
    !data.access?.brand_level ||
    !data.access.brand_level.brands ||
    !Array.isArray(data.access.brand_level.brands) ||
    data.access.brand_level.brands.length === 0
  ) {
    return <span>--</span>;
  }

  const brandIds = data.access.brand_level.brands;
  const access_type = data.access.brand_level.access_mode;

  const brandNames = brandIds.map((brand: any) => {
    const fullBrand = brandsList.find(b => b.id === parseInt(brand.id));
    return fullBrand ? fullBrand.name : brand.name;
  });

  return (
    <div className="flex flex-col gap-1 max-h-[60px] overflow-auto scrollbar-thin">
      {brandNames.length > 0 && (
        <div className="flex gap-1 flex-wrap pr-1 justify-center">
          {access_type === "EXCLUDE" && brandNames.map((name: string, index: number) => (
            <Badge key={`excluded-${index}`} variant="destructive">
              {name}
            </Badge>
          ))}
          {access_type === "INCLUDE" && brandNames.map((name: string, index: number) => (
            <Badge key={`included-${index}`} variant="default">
              {name}
            </Badge>
          ))}
        </div>
      )}
      {brandNames.length === 0 && <span>--</span>}
    </div>
  );
};

const OrganizationCellRenderer = ({ data, organizationsList }: { data: any, organizationsList: any[] }) => {
  if (
    !data ||
    !data.access?.organization_level ||
    !data.access.organization_level.organizations ||
    !Array.isArray(data.access.organization_level.organizations) ||
    data.access.organization_level.organizations.length === 0
  ) {
    return <span>--</span>;
  }

  const organizationIds = data.access.organization_level.organizations;
  const access_type = data.access.organization_level.access_mode;

  const organizationNames = organizationIds.map((organization: any) => {
    const fullOrg = organizationsList.find(o => o.id === parseInt(organization.id));
    return fullOrg ? fullOrg.name : organization.name;
  });

  return (
    <div className="flex flex-col gap-1 max-h-[60px] overflow-auto scrollbar-thin">
      {organizationNames.length > 0 && (
        <div className="flex gap-1 flex-wrap pr-1 justify-center">
          {access_type === "EXCLUDE" && organizationNames.map((name: string, index: number) => (
            <Badge key={`excluded-${index}`} variant="destructive">
              {name}
            </Badge>
          ))}
          {access_type === "INCLUDE" && organizationNames.map((name: string, index: number) => (
            <Badge key={`included-${index}`} variant="default">
              {name}
            </Badge>
          ))}
        </div>
      )}
      {organizationNames.length === 0 && <span>--</span>}
    </div>
  );
};


const Items = ({
  toggle,
  channels,
  groupings,
  component,
  customComponents,
  reports,
  reportsSandbox,
  openDialog,
  setOpenDialog,
  brands,
  organizations,
  searchText,
  applications,
  hasEditAccess = true,
  hasManageAccess = true,
}: ItemsProps) => {
  const showActionsColumn = hasEditAccess || hasManageAccess;
  const [loadData, setLoadData] = useState<boolean>(false);
  const [drawerItem, setDrawerItems] = useState<any>([]);
  const [drawerItemBackUp, setDrawerItemsBackUp] = useState<any>([]);
  const [formState, setFormState] = useState<FormState>(initialFormState);
  const [formStateEdit, setFormStateEdit] =
    useState<FormStateEdit>(initialFormStateEdit);
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [currentData, setCurrentData] = useState<any>(null);
  const [editTeamAcessCheck, setEditTeamAcessCheck] = useState<any>({
    cuurnet: null,
  });
  const [openTeamChangeDialog, setOpenTeamChangeDialog] =
    useState<boolean>(false);
  const [searchedResults, setSearchedResults] = useState<any>([]);

  const user = JSON.parse(localStorage.getItem("user") || "");
  const [existing, setExisting] = useState<any>(null);

  // State for display order management
  const [makeItTopAdd, setMakeItTopAdd] = useState<boolean>(false);
  const [makeItBelowAdd, setMakeItBelowAdd] = useState<string>("");
  const [makeItTopEdit, setMakeItTopEdit] = useState<boolean>(false);
  const [makeItBelowEdit, setMakeItBelowEdit] = useState<string>("");

  // State for hierarchical data
  const [groupingsWithChildren, setGroupingsWithChildren] = useState<any>([]);
  const [channelsWithChildren, setChannelsWithChildren] = useState<any>([]);

  // State for "Set as Default" functionality
  const [setAsDefaultAdd, setSetAsDefaultAdd] = useState<boolean>(false);
  const [setAsDefaultEdit, setSetAsDefaultEdit] = useState<boolean>(false);
  const [defaultItems, setDefaultItems] = useState<any[]>([]);

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Filter state (Application, Organization, Brand) - single select to match reports tab style
  const [filterApplicationId, setFilterApplicationId] = useState<string>("");
  const [filterOrganizationId, setFilterOrganizationId] = useState<string>("");
  const [filterBrandId, setFilterBrandId] = useState<string>("");

  // Brands and organizations for filter dropdowns (loaded when tab is active; same API as default-sidebar-items)
  const [filterBrands, setFilterBrands] = useState<any[]>([]);
  const [filterOrganizations, setFilterOrganizations] = useState<any[]>([]);

  // Column visibility (select which columns to show in table)
  const ITEMS_SELECTABLE_COLUMN_KEYS = [
    "application", "channel", "grouping", "display_name", "reports",
    "organizations", "brands", "status", "ui_component", "custom_components",
    "route_endpoint", "params", "created_at",
  ];
  const [selectedItemColumns, setSelectedItemColumns] = useState<string[]>(() =>
    ITEMS_SELECTABLE_COLUMN_KEYS.filter((k) => !["created_at", "params", "route_endpoint"].includes(k))
  );

  const handleFormChange = (field: keyof FormState, value: any) => {
    setFormState((prev) => {
      const newState = { ...prev, [field]: value };

      // Clear params when UI component changes
      if (field === "selectedUIComponents") {
        newState.params = [];
        newState.selectedReports = [];
        newState.selectedSandbox = null;
      }

      // Special handling for selectedReports - auto-select TableauWrapper component
      // if (field === "selectedReports") {
      //   newState.selectedUIComponents = component.find(
      //     (sr: any) => sr?.name === "TableauWrapper"
      //   );
      // }

      return newState;
    });
  };
  const handleFormEditChange = (field: keyof FormStateEdit, value: any) => {
    setFormStateEdit((prev) => {
      const newState = { ...prev, [field]: value };

      // Clear params when UI component changes
      if (field === "selectedUIComponents") {
        newState.params = [];
        newState.selectedReports = [];
        newState.selectedSandbox = null;
      }

      // Special handling for selectedReports - auto-select TableauWrapper component
      // if (field === "selectedReports") {
      //   newState.selectedUIComponents = component.find(
      //     (sr: any) => sr?.name === "TableauWrapper"
      //   );
      // }

      return newState;
    });
  };

  const getGroupingsWithChildren = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      const response = await apiClientDrawerV2.get('/admin/groupings', {
        params: {
          include_inactive: true,
          user_id: user.id,
          is_admin: true,
          is_team: false,
        }
      });

      const groupingsData = response.data.sort((a: any, b: any) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setGroupingsWithChildren(groupingsData);
    } catch (error) {
      console.error('Failed to fetch groupings:', error);
    }
  };

  const getChannelsWithChildren = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      const response = await apiClientDrawerV2.get('/admin/channels', {
        params: {
          include_inactive: true,
          user_id: user.id,
          is_admin: true,
          is_team: false,
        }
      });

      const channelsData = response.data.sort((a: any, b: any) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setChannelsWithChildren(channelsData);
    } catch (error) {
      console.error('Failed to fetch channels:', error);
    }
  };

  // Function to fetch default items
  const getDefaultItems = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      const params: any = {
        user_id: user.id,
        is_admin: user.is_admin || false,
        is_team: user.is_team || false,
      };

      if (user.brand_id !== undefined && user.brand_id !== null && user.brand_id !== "") {
        params.brand_id = user.brand_id;
      }

      const response = await apiClientDrawerV2.get('/admin/default-items', { params });
      setDefaultItems(response.data.filter((item: any) => item.user_id === null) || []);
    } catch (error) {
      console.error('Error loading default items:', error);
      setDefaultItems([]);
    }
  };

  const getDrawerItems = async () => {
    try {
      // Get user info from localStorage
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      const res = await apiClientDrawerV2.get("/admin/items", {
        params: {
          include_inactive: true,
          user_id: user.id,
          is_admin: true,
          is_team: false,
        },
      });

      const sortedDrawerItems = res?.data.sort(
        (
          a: { created_at: string | number | Date },
          b: { created_at: string | number | Date }
        ) => {
          return (
            (new Date(b.created_at) as any) - (new Date(a.created_at) as any)
          );
        }
      );

      if (searchText.trim().length === 0) {
        setDrawerItems(sortedDrawerItems);
      } else {
        setDrawerItems(
          sortedDrawerItems.filter((item: any) =>
            searchedResults.includes(item.id)
          )
        );
      }

      setDrawerItemsBackUp(sortedDrawerItems);
    } catch (error) {
      console.error("Error fetching items:", error);
      Toastify("error", "Items not fetched");
    } finally {
    }
  };

  useEffect(() => {
    if (toggle === "items") {
      getLatestData();
      loadFilterBrandsAndOrganizations();
    }
  }, [toggle]);

  const loadFilterBrandsAndOrganizations = async () => {
    try {
      const brandsRes = await apiClientBrand.get("/brand/v1/admin/brands/all");
      setFilterBrands(brandsRes.data?.response || []);
      const orgsRes = await apiClientBrand.get("/brand/v1/organizations/all");
      setFilterOrganizations(orgsRes.data?.response || []);
    } catch (error) {
      console.error("Failed to load brands/organizations for filters:", error);
      setFilterBrands([]);
      setFilterOrganizations([]);
    }
  };

  const getLatestData = async () => {
    try {
      setLoadData(true);
      await getGroupingsWithChildren();
      await getChannelsWithChildren();
      await getDrawerItems();
      await getDefaultItems();
    } catch (error) {
      console.error("Error fetching latest data:", error);
    }
    finally {
      setLoadData(false);
    }
  }

  const channelMapping = channels?.reduce((acc, channel) => {
    acc[channel.id] = channel.name;
    return acc;
  }, {});

  const getApplicationName = (row: any) => {
    if (!row?.channel_id) return '--';
    const channel = (channels || []).find((c: any) => String(c.id) === String(row.channel_id));
    if (!channel?.application_id) return '--';
    const app = (applications || []).find((a: any) => String(a.id) === String(channel.application_id));
    return (app as any)?.display_name || app?.name || '--';
  };

  const groupingsMapping = groupings?.reduce((acc, grp) => {
    acc[grp.id] = grp.name;
    return acc;
  }, {});

  const componentMapping = component?.reduce((acc, comp) => {
    acc[comp.id] = comp.name;
    return acc;
  }, {});

  const customComponentsMapping = customComponents?.reduce((acc, cusComp) => {
    acc[cusComp.id] = cusComp.name;
    return acc;
  }, {});

  const reportsMapping = reports?.reduce((acc, report) => {
    acc[report.id] = report.display_name;
    return acc;
  }, {});

  // Get available items for "Move to below" dropdown in Add dialog
  const availableItemsForBelowAdd = useMemo(() => {
    if (!formState.selectedChannel) return [];

    // Case 1: Grouping is selected - use grouping's children
    if (formState.selectedGroupings) {
      const selectedGrouping = groupingsWithChildren.find((grouping: any) => grouping.id === formState.selectedGroupings.id);
      if (selectedGrouping && selectedGrouping.children) {
        return selectedGrouping.children.sort((a: any, b: any) => (a.display_order || 0) - (b.display_order || 0));
      }

      // Fallback: use drawerItem filtered by grouping
      return drawerItem
        .filter((item: any) => item.grouping_id === formState.selectedGroupings.id)
        .sort((a: any, b: any) => (a.display_order || 0) - (b.display_order || 0));
    }

    // Case 2: No grouping selected - use channel's children (items without grouping)
    const selectedChannel = channelsWithChildren.find((channel: any) => channel.id === formState.selectedChannel.id);
    if (selectedChannel && selectedChannel.children) {
      // Filter out items that have groupings (we only want direct channel items)
      return selectedChannel.children
        .filter((item: any) => !item.grouping_id)
        .sort((a: any, b: any) => (a.display_order || 0) - (b.display_order || 0));
    }

    // Fallback: use drawerItem filtered by channel and no grouping
    return drawerItem
      .filter((item: any) => item.channel_id === formState.selectedChannel.id && !item.grouping_id)
      .sort((a: any, b: any) => (a.display_order || 0) - (b.display_order || 0));
  }, [groupingsWithChildren, channelsWithChildren, formState.selectedChannel, formState.selectedGroupings, drawerItem]);

  // Get available items for "Move to below" dropdown in Edit dialog
  const availableItemsForBelowEdit = useMemo(() => {
    if (!formStateEdit.selectedChannel || !currentData?.id) return [];

    // Case 1: Grouping is selected - use grouping's children
    if (formStateEdit.selectedGroupings) {
      const selectedGrouping = groupingsWithChildren.find((grouping: any) => grouping.id === formStateEdit.selectedGroupings.id);
      if (selectedGrouping && selectedGrouping.children) {
        return selectedGrouping.children
          .filter((item: any) => item.id !== currentData.id)
          .sort((a: any, b: any) => (a.display_order || 0) - (b.display_order || 0));
      }

      // Fallback: use drawerItem filtered by grouping
      return drawerItem
        .filter((item: any) => item.grouping_id === formStateEdit.selectedGroupings.id && item.id !== currentData.id)
        .sort((a: any, b: any) => (a.display_order || 0) - (b.display_order || 0));
    }

    // Case 2: No grouping selected - use channel's children (items without grouping)
    const selectedChannel = channelsWithChildren.find((channel: any) => channel.id === formStateEdit.selectedChannel.id);
    if (selectedChannel && selectedChannel.children) {
      // Filter out items that have groupings (we only want direct channel items)
      return selectedChannel.children
        .filter((item: any) => !item.grouping_id && item.id !== currentData.id)
        .sort((a: any, b: any) => (a.display_order || 0) - (b.display_order || 0));
    }

    // Fallback: use drawerItem filtered by channel and no grouping
    return drawerItem
      .filter((item: any) =>
        item.channel_id === formStateEdit.selectedChannel.id &&
        !item.grouping_id &&
        item.id !== currentData.id
      )
      .sort((a: any, b: any) => (a.display_order || 0) - (b.display_order || 0));
  }, [groupingsWithChildren, channelsWithChildren, formStateEdit.selectedChannel, formStateEdit.selectedGroupings, currentData?.id, drawerItem]);

  const handleDelete = async (data: any) => {
    try {
      // Get user info from localStorage
      setLoadData(true);
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      const res = await apiClientDrawerV2.delete(`/admin/items/${data?.id}`, {
        params: {
          user_id: user.id,
          brand_id: user.brand_id,
          is_admin: true,
          is_team: false,
        },
      });

      getLatestData();
      setOpenDeleteDialog(false);
      Toastify("success", "Item deleted successfully");

      const resolveChannelName = (id: any) =>
        channelMapping?.[id] ||
        channels?.find((c: any) => String(c.id) === String(id))?.name ||
        (id != null && id !== "" ? String(id) : "Not Set");
      const resolveGroupingName = (id: any) =>
        id
          ? groupingsMapping?.[id] ||
            groupings?.find((g: any) => String(g.id) === String(id))?.name ||
            String(id)
          : "None";
      const resolveComponentName = (id: any) =>
        componentMapping?.[id] ||
        component?.find((c: any) => String(c.id) === String(id))?.name ||
        (id != null && id !== "" ? String(id) : "Not Set");

      const details: Array<{ label: string; value: string }> = [
        { label: "Item ID", value: String(data?.id ?? "Not Set") },
        { label: "Channel", value: resolveChannelName(data?.channel_id) },
        { label: "Grouping", value: resolveGroupingName(data?.grouping_id) },
        {
          label: "UI Component",
          value: resolveComponentName(data?.ui_component_id),
        },
      ];

      const deletedBrands = data?.access?.brand_level?.brands || [];
      if (deletedBrands.length) {
        const accessMode = data?.access?.brand_level?.access_mode;
        details.push({
          label: "Brand Access",
          value: deletedBrands
            .map((brand: any) =>
              resolveAccessEntryLabel(
                accessMode === "EXCLUDE"
                  ? -parseInt(brand.id)
                  : parseInt(brand.id),
                [...(brands || []), ...deletedBrands],
                "Brand"
              )
            )
            .join(", "),
        });
      }

      const deletedOrgs =
        data?.access?.organization_level?.organizations || [];
      if (deletedOrgs.length) {
        const accessMode = data?.access?.organization_level?.access_mode;
        details.push({
          label: "Organization Access",
          value: deletedOrgs
            .map((org: any) =>
              resolveAccessEntryLabel(
                accessMode === "EXCLUDE"
                  ? -parseInt(org.id)
                  : parseInt(org.id),
                [...(organizations || []), ...deletedOrgs],
                "Organization"
              )
            )
            .join(", "),
        });
      }

      const desc = {
        message: buildSummary("Drawer item", data?.display_name, "deleted"),
        status: res.status,
        method: res.config.method,
        url: res.config.url,
        category: "drawer",
        action: "delete_item",
        item_id: data?.id,
        item_name: data?.display_name,
        user_id: user?.id,
        user_name: user?.name,
        details,
      };

      await actionLog?.actionLogTrack(JSON.stringify(desc), false);
    } catch (error: any) {
      console.error("Error deleting item:", error);
      Toastify("error", "Item not deleted");
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const errorDesc = {
        message: `Failed to delete Drawer item (Item ID: ${data?.id ?? "N/A"}, Item Name: "${data?.display_name || ""}") by User (User ID: ${user?.id}, User Name: "${user?.name}")`,
        status: error?.response?.status,
        method: "DELETE",
        url: `/admin/items/${data?.id}`,
        category: "drawer",
        action: "delete_item",
        item_id: data?.id,
        item_name: data?.display_name,
        user_id: user?.id,
        user_name: user?.name,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
    } finally {
      setCurrentData(null);
      setLoadData(false);
    }
  };

  const filteredData = useMemo(() => {
    let list = drawerItem;
    if (filterApplicationId) {
      list = list.filter((item: any) => {
        const channel = (channels || []).find((c: any) => String(c.id) === String(item.channel_id));
        return channel && String(channel.application_id) === filterApplicationId;
      });
    }
    if (filterOrganizationId) {
      list = list.filter((item: any) => {
        const orgsList = item?.access?.organization_level?.organizations;
        if (!orgsList || !Array.isArray(orgsList)) return false;
        return orgsList.some((o: any) => String(o.id) === filterOrganizationId);
      });
    }
    if (filterBrandId) {
      list = list.filter((item: any) => {
        const brandsList = item?.access?.brand_level?.brands;
        if (!brandsList || !Array.isArray(brandsList)) return false;
        return brandsList.some((b: any) => String(b.id) === filterBrandId);
      });
    }
    return list;
  }, [drawerItem, channels, filterApplicationId, filterOrganizationId, filterBrandId]);

  // --- Progressive filter options ---
  // Application options: derived from all items in the table (no filter applied yet)
  const availableApplicationOptions = useMemo(() => {
    const appIds = new Set<string>();
    drawerItem.forEach((item: any) => {
      const channel = (channels || []).find((c: any) => String(c.id) === String(item.channel_id));
      if (channel?.application_id) appIds.add(String(channel.application_id));
    });
    return (applications || [])
      .filter((a: any) => appIds.has(String(a.id)))
      .map((a: any) => ({ label: (a as any).display_name || a.name || String(a.id), value: String(a.id) }));
  }, [drawerItem, channels, applications]);

  // After application filter: items visible given current app selection
  const itemsAfterAppFilter = useMemo(() => {
    if (!filterApplicationId) return drawerItem;
    return drawerItem.filter((item: any) => {
      const channel = (channels || []).find((c: any) => String(c.id) === String(item.channel_id));
      return channel && String(channel.application_id) === filterApplicationId;
    });
  }, [drawerItem, channels, filterApplicationId]);

  // Organization options: derived from items visible after app filter
  const availableOrganizationOptions = useMemo(() => {
    const orgMap = new Map<string, string>();
    itemsAfterAppFilter.forEach((item: any) => {
      const orgsList: any[] = item?.access?.organization_level?.organizations || [];
      orgsList.forEach((o: any) => orgMap.set(String(o.id), o.name ?? String(o.id)));
    });
    return Array.from(orgMap.entries()).map(([value, label]) => ({ label, value }));
  }, [itemsAfterAppFilter]);

  // After app + org filter: items visible given current app + org selection
  const itemsAfterAppOrgFilter = useMemo(() => {
    if (!filterOrganizationId) return itemsAfterAppFilter;
    return itemsAfterAppFilter.filter((item: any) => {
      const orgsList = item?.access?.organization_level?.organizations;
      if (!orgsList || !Array.isArray(orgsList)) return false;
      return orgsList.some((o: any) => String(o.id) === filterOrganizationId);
    });
  }, [itemsAfterAppFilter, filterOrganizationId]);

  // Brand options: derived from items visible after app + org filters
  const availableBrandOptions = useMemo(() => {
    const brandMap = new Map<string, string>();
    itemsAfterAppOrgFilter.forEach((item: any) => {
      const brandsList: any[] = item?.access?.brand_level?.brands || [];
      brandsList.forEach((b: any) => brandMap.set(String(b.id), b.name ?? String(b.id)));
    });
    return Array.from(brandMap.entries()).map(([value, label]) => ({ label, value }));
  }, [itemsAfterAppOrgFilter]);

  useEffect(() => {
    setPage(0);
  }, [filterApplicationId, filterOrganizationId, filterBrandId]);

  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (value: string) => {
    setRowsPerPage(parseInt(value, 10));
    setPage(0); // Reset to first page when rows per page changes
  };

  const handleButtonClick = () => {
    setOpenDialog(true);
  };

  const handleUpsertItem = async (isEdit: boolean = false) => {
    try {
      setLoadData(true);
      const formData = isEdit ? formStateEdit : formState;

      if (!validateForm(formData)) {
        return;
      }

      // Additional required field validations
      if (!formData.selectedApplication) {
        Toastify("error", "Please select an application");
        return;
      }

      if (!formData.selectedChannel) {
        Toastify("error", "Please select a channel");
        return;
      }

      if (!formData.selectedUIComponents) {
        Toastify("error", "Please select a UI component");
        return;
      }

      // Validate display order selection
      const currentMakeItTop = isEdit ? makeItTopEdit : makeItTopAdd;
      const currentMakeItBelow = isEdit ? makeItBelowEdit : makeItBelowAdd;

      if (!currentMakeItTop && !currentMakeItBelow) {
        Toastify("error", "Please either select 'Move to top' or choose an item in 'Move to below'");
        return;
      }

      if (
        formData.levelAccess === "Brand" &&
        formData.selectedBrands.length === 0
      ) {
        Toastify(
          "error",
          "Please select at least one brand for Brand level access"
        );
        return;
      }

      if (
        formData.levelAccess === "Organization" &&
        formData.selectedOrganizations.length === 0
      ) {
        Toastify(
          "error",
          "Please select at least one organization for Organization level access"
        );
        return;
      }

      // Get user info from localStorage
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      // Calculate new display_order based on user selection
      const selectedMakeItTop = isEdit ? makeItTopEdit : makeItTopAdd;
      const selectedMakeItBelow = isEdit ? makeItBelowEdit : makeItBelowAdd;
      const availableItems = isEdit ? availableItemsForBelowEdit : availableItemsForBelowAdd;

      let newDisplayOrder = 0;

      if (selectedMakeItTop) {
        newDisplayOrder = 0;
      } else if (selectedMakeItBelow && availableItems.length > 0) {
        const selectedItem = availableItems.find((item: any) => item.id === selectedMakeItBelow);
        if (selectedItem) {
          newDisplayOrder = (selectedItem.display_order || 0) + 1;
        }
      }

      const payload = {
        id: isEdit ? (formData as FormStateEdit).id : null,
        channel_id: formData.selectedChannel?.id,
        grouping_id: formData.selectedGroupings?.id || null,
        display_name: formData.display_name || "",
        icon: formData.icon || null,
        route_endpoint: createEndPoint(formData) + formData.route_endpoint,
        ui_component_id: formData.selectedUIComponents?.id,
        is_grouped: !!formData.selectedGroupings?.id,
        is_active: formData.is_active,
        is_team: formData.isTeam,
        is_restricted: formData.isRestricted,
        close_sidebar_on_navigate: !!formData.closeSidebarOnNavigate,
        report_config: [
          ...(formData.selectedReports || []).map((report) => report.id),
          ...(formData.selectedSandbox ? [formData.selectedSandbox.id] : [])
        ],
        custom_component_config: formData.selectedCustomComponents.map(
          (component) => component.id
        ),
        params:
          formData.selectedUIComponents?.name === "SuperComponent"
            ? transformData(formData.params)
            : Array.isArray(formData.params)
              ? formData.params.toString().split(",").map(Number)
              : formData.params && typeof formData.params === 'string'
                ? formData.params.split(",").map(Number)
                : formData.params,
        report_type: formData.report_type as any,
        display_order: newDisplayOrder,
        below_item: selectedMakeItTop ? null : selectedMakeItBelow,
      };

      console.log(payload);
      // return;
      const res = await apiClientDrawerV2.post("/admin/items", payload, {
        params: {
          user_id: user.id,
          brand_id: user.brand_id,
          is_admin: true,
          is_team: false,
        },
      });

      const itemId = isEdit ? (formData as FormStateEdit).id : res.data?.id;

      // Handle brand access for Default report type
      if (formData.report_type === "Default" && itemId) {
        await handleBrandAccess(formData, itemId, user, isEdit);
      }

      // Handle organization access for Default report type
      if (formData.report_type === "Default" && itemId) {
        await handleOrganizationAccess(formData, itemId, user, isEdit);
      }

      // Handle "Set as Default" functionality
      const shouldSetAsDefault = isEdit ? setAsDefaultEdit : setAsDefaultAdd;
      if (itemId) {
        await handleDefaultItemCreation(itemId, formData, shouldSetAsDefault);
      }

      getLatestData();

      if (isEdit) {
        handleEditDialogClose();
        Toastify("success", "Item updated successfully");
      } else {
        handleDialogClose();
        Toastify("success", "Item created successfully");
      }

      const resolveChannelName = (id: any) =>
        channelMapping?.[id] ||
        channels?.find((c: any) => String(c.id) === String(id))?.name ||
        (id != null && id !== "" ? String(id) : "Not Set");
      const resolveGroupingName = (id: any) =>
        id
          ? groupingsMapping?.[id] ||
            groupings?.find((g: any) => String(g.id) === String(id))?.name ||
            String(id)
          : "None";
      const resolveComponentName = (id: any) =>
        componentMapping?.[id] ||
        component?.find((c: any) => String(c.id) === String(id))?.name ||
        (id != null && id !== "" ? String(id) : "Not Set");
      const resolveReportName = (id: any) =>
        reportsMapping?.[id] ||
        reports?.find((r: any) => String(r.id) === String(id))?.display_name ||
        reportsSandbox?.find((r: any) => String(r.id) === String(id))
          ?.display_name ||
        (id != null && id !== "" ? String(id) : "Not Set");
      const resolveCustomComponentName = (id: any) =>
        customComponentsMapping?.[id] ||
        customComponents?.find((c: any) => String(c.id) === String(id))
          ?.name ||
        (id != null && id !== "" ? String(id) : "Not Set");
      // Brand/org access resolvers live in buildAccessListChange (Exclude/Include + name).

      const itemFieldSpecs: FieldSpec[] = [
        { key: "display_name", label: "Display Name" },
        {
          key: "channel_id",
          label: "Channel",
          resolve: resolveChannelName,
        },
        {
          key: "grouping_id",
          label: "Grouping",
          resolve: resolveGroupingName,
        },
        {
          key: "ui_component_id",
          label: "UI Component",
          resolve: resolveComponentName,
        },
        { key: "route_endpoint", label: "Route" },
        { key: "icon", label: "Icon" },
        {
          key: "is_active",
          label: "Status",
          type: "boolean",
          boolStyle: "activeInactive",
        },
        {
          key: "is_team",
          label: "Team Access",
          type: "boolean",
          boolStyle: "yesNo",
        },
        {
          key: "is_restricted",
          label: "Restricted",
          type: "boolean",
          boolStyle: "yesNo",
        },
        {
          key: "close_sidebar_on_navigate",
          label: "Close Sidebar After Opening",
          type: "boolean",
          boolStyle: "yesNo",
        },
        { key: "report_type", label: "Report Type" },
        {
          key: "report_config",
          label: "Reports",
          type: "list",
          resolve: resolveReportName,
        },
        {
          key: "custom_component_config",
          label: "Custom Components",
          type: "list",
          resolve: resolveCustomComponentName,
        },
        { key: "display_order", label: "Display Order", type: "number" },
      ];

      const normalizedItem = {
        display_name: payload.display_name,
        channel_id: payload.channel_id,
        grouping_id: payload.grouping_id || null,
        ui_component_id: payload.ui_component_id,
        route_endpoint: payload.route_endpoint,
        icon: payload.icon || null,
        is_active: payload.is_active,
        is_team: payload.is_team,
        is_restricted: payload.is_restricted,
        close_sidebar_on_navigate: payload.close_sidebar_on_navigate,
        report_type: payload.report_type,
        report_config: payload.report_config || [],
        custom_component_config: payload.custom_component_config || [],
        display_order: payload.display_order ?? 0,
      };

      const previousItem = {
        display_name: currentData?.display_name,
        channel_id: currentData?.channel_id,
        grouping_id: currentData?.grouping_id || null,
        ui_component_id: currentData?.ui_component_id,
        route_endpoint: currentData?.route_endpoint,
        icon: currentData?.icon || null,
        is_active: currentData?.is_active,
        is_team: currentData?.is_team,
        is_restricted: currentData?.is_restricted || false,
        close_sidebar_on_navigate:
          currentData?.extra_metadata?.close_sidebar_on_navigate ??
          currentData?.close_sidebar_on_navigate ??
          false,
        report_type: currentData?.report_type,
        report_config: currentData?.report_config || [],
        custom_component_config: currentData?.custom_component_config || [],
        display_order: currentData?.display_order ?? 0,
      };

      const changes = isEdit
        ? diffFields(previousItem, normalizedItem, itemFieldSpecs)
        : [];

      if (isEdit && formData.report_type === "Default") {
        const originalBrands =
          currentData?.access?.brand_level?.brands || [];
        const originalBrandIds = originalBrands.map((brand: any) => {
          const accessMode = currentData?.access?.brand_level?.access_mode;
          return accessMode === "EXCLUDE"
            ? -parseInt(brand.id)
            : parseInt(brand.id);
        });
        const brandChange = buildAccessListChange(
          "Brand Access",
          originalBrandIds,
          formData.brands_exclude || [],
          [...(brands || []), ...originalBrands],
          "Brand"
        );
        if (brandChange) changes.push(brandChange);

        const originalOrgs =
          currentData?.access?.organization_level?.organizations || [];
        const originalOrgIds = originalOrgs.map((org: any) => {
          const accessMode =
            currentData?.access?.organization_level?.access_mode;
          return accessMode === "EXCLUDE"
            ? -parseInt(org.id)
            : parseInt(org.id);
        });
        const orgChange = buildAccessListChange(
          "Organization Access",
          originalOrgIds,
          formData.organizations_exclude || [],
          [...(organizations || []), ...originalOrgs],
          "Organization"
        );
        if (orgChange) changes.push(orgChange);
      }

      const desc = {
        message: isEdit
          ? buildSummary(
              "Drawer item",
              payload?.display_name,
              "updated",
              changes.length
            )
          : buildSummary("Drawer item", payload?.display_name, "created"),
        status: res.status,
        method: res.config.method,
        url: res.config.url,
        category: "drawer",
        action: isEdit ? "update_item" : "create_item",
        item_id: itemId || null,
        item_name: payload?.display_name,
        user_id: user?.id,
        user_name: user?.name,
        ...(isEdit
          ? { changes }
          : {
              details: [
                ...buildDetails(normalizedItem, itemFieldSpecs, {
                  skipEmpty: true,
                }),
                ...(formData.report_type === "Default" &&
                (formData.brands_exclude || []).length
                  ? [
                      {
                        label: "Brand Access",
                        value: (formData.brands_exclude || [])
                          .map((id: any) =>
                            resolveAccessEntryLabel(id, brands || [], "Brand")
                          )
                          .join(", "),
                      },
                    ]
                  : []),
                ...(formData.report_type === "Default" &&
                (formData.organizations_exclude || []).length
                  ? [
                      {
                        label: "Organization Access",
                        value: (formData.organizations_exclude || [])
                          .map((id: any) =>
                            resolveAccessEntryLabel(
                              id,
                              organizations || [],
                              "Organization"
                            )
                          )
                          .join(", "),
                      },
                    ]
                  : []),
              ],
            }),
      };

      if (isEdit) {
        setCurrentData(null);
      }

      await actionLog?.actionLogTrack(JSON.stringify(desc), false);
    } catch (err: any) {
      console.error(`Error ${isEdit ? 'updating' : 'creating'} item:`, err);
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const currentFormData = isEdit ? formStateEdit : formState;
      const itemId = isEdit ? (currentFormData as FormStateEdit).id : null;
      const itemName = currentFormData.display_name || "";
      const action = isEdit ? "update_item" : "create_item";
      if (err.response?.data?.detail) {
        const errorMessages = Array.isArray(err.response.data.detail) ? err.response.data.detail.map((error: any) => `${error.loc?.join(".")}: ${error.msg}`).join(", ") : err.response.data.detail;
        Toastify("error", errorMessages);
        const errorDesc = {
          message: `Failed to ${isEdit ? 'update' : 'create'} Item (Item ID: ${itemId || 'N/A'}, Item Name: "${itemName}") by User (User ID: ${user?.id}, User Name: "${user?.name}"). Error: ${errorMessages}`,
          status: err.response?.status,
          method: "POST",
          url: "/admin/items",
          category: "drawer",
          action,
          item_id: itemId || null,
          item_name: itemName,
          user_id: user?.id,
          user_name: user?.name,
        };
        await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
      } else {
        Toastify(
          "error",
          err.response?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} item`
        );
        const errorDesc = {
          message: `Failed to ${isEdit ? 'update' : 'create'} Item (Item ID: ${itemId || 'N/A'}, Item Name: "${itemName}") by User (User ID: ${user?.id}, User Name: "${user?.name}"). Error: ${err.response?.data?.message || err.message}`,
          status: err.response?.status,
          method: "POST",
          url: "/admin/items",
          category: "drawer",
          action,
          item_id: itemId || null,
          item_name: itemName,
          user_id: user?.id,
          user_name: user?.name,
        };
        await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
      }
    } finally {
      setLoadData(false);
    }
  };

  const handleBrandAccess = async (formData: any, itemId: string, user: any, isEdit: boolean) => {
    if (isEdit) {
      // Handle brand access changes for edit
      const originalBrands = currentData?.access?.brand_level?.brands || [];
      const originalBrandsExclude = originalBrands.map((brand: any) => {
        const accessMode = currentData?.access?.brand_level?.access_mode;
        return accessMode === "EXCLUDE"
          ? -parseInt(brand.id)
          : parseInt(brand.id);
      });

      const brandChanged =
        JSON.stringify(originalBrandsExclude.sort()) !==
        JSON.stringify(formData.brands_exclude.sort());

      if (!brandChanged) return;

      const originalAccessMode =
        currentData?.access?.brand_level?.access_mode || "INCLUDE";
      const originalBrandIds = originalBrandsExclude.map((id: number) =>
        Math.abs(id).toString()
      );

      const excludedBrands = formData.brands_exclude
        .filter((id: number) => id < 0)
        .map((id: number) => Math.abs(id));
      const includedBrands = formData.brands_exclude.filter(
        (id: number) => id > 0
      );

      let newAccessMode: "INCLUDE" | "EXCLUDE" = "INCLUDE";
      let newBrandIds: string[] = [];

      if (excludedBrands.length > 0) {
        newAccessMode = "EXCLUDE";
        newBrandIds = excludedBrands.map((id: number) => id.toString());
      } else if (includedBrands.length > 0) {
        newAccessMode = "INCLUDE";
        newBrandIds = includedBrands.map((id: number) => id.toString());
      }

      let brandAccessData: any[] = [];

      if (originalAccessMode !== newAccessMode) {
        // Remove all brands from the old access mode
        for (const brandId of originalBrandIds) {
          brandAccessData.push({
            brand_id: brandId,
            level_type: "item",
            level_ids: [itemId],
            access_mode: originalAccessMode,
            is_wildcard: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            created_by: user.id,
            modified_by: user.id,
            action: "remove",
          });
        }

        // Add all brands to the new access mode
        for (const brandId of newBrandIds) {
          brandAccessData.push({
            brand_id: brandId,
            level_type: "item",
            level_ids: [itemId],
            access_mode: newAccessMode,
            is_wildcard: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            created_by: user.id,
            modified_by: user.id,
            action: "add",
          });
        }
      } else {
        // Same access mode - handle additions and removals
        const addedBrands = newBrandIds.filter(
          (id: string) => !originalBrandIds.includes(id)
        );
        const removedBrands = originalBrandIds.filter(
          (id: string) => !newBrandIds.includes(id)
        );

        for (const brandId of addedBrands) {
          brandAccessData.push({
            brand_id: brandId,
            level_type: "item",
            level_ids: [itemId],
            access_mode: newAccessMode,
            is_wildcard: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            created_by: user.id,
            modified_by: user.id,
            action: "add",
          });
        }

        for (const brandId of removedBrands) {
          brandAccessData.push({
            brand_id: brandId,
            level_type: "item",
            level_ids: [itemId],
            access_mode: newAccessMode,
            is_wildcard: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            created_by: user.id,
            modified_by: user.id,
            action: "remove",
          });
        }
      }

      if (brandAccessData.length > 0) {
        try {
          await apiClientDrawerV2.post(
            `/admin/brand-access?user_id=${user.id}`,
            {
              data: brandAccessData,
            }
          );
        } catch (brandAccessError) {
          console.error("Error updating brand access:", brandAccessError);
          Toastify("warning", "Brand access update failed");
        }
      }
    } else {
      // Handle brand access for new item
      const excludedBrands = formData.brands_exclude
        .filter((id: number) => id < 0)
        .map((id: number) => Math.abs(id));
      const includedBrands = formData.brands_exclude.filter(
        (id: number) => id > 0
      );

      let accessMode: "INCLUDE" | "EXCLUDE" = "INCLUDE";
      let brandIds: string[] = [];

      if (excludedBrands.length > 0) {
        accessMode = "EXCLUDE";
        brandIds = excludedBrands.map((id: number) => id.toString());
      } else if (includedBrands.length > 0) {
        accessMode = "INCLUDE";
        brandIds = includedBrands.map((id: number) => id.toString());
      }

      if (brandIds.length > 0) {
        const brandAccessData = brandIds.map((brandId: string) => ({
          brand_id: brandId,
          level_type: "item",
          level_ids: [itemId],
          access_mode: accessMode,
          is_wildcard: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          created_by: user.id,
          modified_by: user.id,
          action: "add",
        }));

        try {
          await apiClientDrawerV2.post(
            `/admin/brand-access?user_id=${user.id}`,
            {
              data: brandAccessData,
            }
          );
        } catch (brandAccessError) {
          console.error("Error creating brand access:", brandAccessError);
          Toastify("warning", "Brand access creation failed");
        }
      }
    }
  };

  const handleOrganizationAccess = async (formData: any, itemId: string, user: any, isEdit: boolean) => {
    if (isEdit) {
      // Handle organization access changes for edit
      const originalOrganizations =
        currentData?.access?.organization_level?.organizations || [];
      const originalOrganizationsExclude = originalOrganizations.map(
        (org: any) => {
          const accessMode =
            currentData?.access?.organization_level?.access_mode;
          return accessMode === "EXCLUDE"
            ? -parseInt(org.id)
            : parseInt(org.id);
        }
      );

      const organizationChanged =
        JSON.stringify(originalOrganizationsExclude.sort()) !==
        JSON.stringify(formData.organizations_exclude.sort());

      if (!organizationChanged) return;

      const originalAccessMode =
        currentData?.access?.organization_level?.access_mode || "INCLUDE";
      const originalOrganizationIds = originalOrganizationsExclude.map(
        (id: number) => Math.abs(id).toString()
      );

      const excludedOrganizations = formData.organizations_exclude
        .filter((id: number) => id < 0)
        .map((id: number) => Math.abs(id));
      const includedOrganizations = formData.organizations_exclude.filter(
        (id: number) => id > 0
      );

      let newAccessMode: "INCLUDE" | "EXCLUDE" = "INCLUDE";
      let newOrganizationIds: string[] = [];

      if (excludedOrganizations.length > 0) {
        newAccessMode = "EXCLUDE";
        newOrganizationIds = excludedOrganizations.map((id: number) =>
          id.toString()
        );
      } else if (includedOrganizations.length > 0) {
        newAccessMode = "INCLUDE";
        newOrganizationIds = includedOrganizations.map((id: number) =>
          id.toString()
        );
      }

      let organizationAccessData: any[] = [];

      if (originalAccessMode !== newAccessMode) {
        // Remove all organizations from the old access mode
        for (const organizationId of originalOrganizationIds) {
          organizationAccessData.push({
            organization_id: organizationId,
            level_type: "item",
            level_ids: [itemId],
            access_mode: originalAccessMode,
            is_wildcard: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            created_by: user.id,
            modified_by: user.id,
            action: "remove",
          });
        }

        // Add all organizations to the new access mode
        for (const organizationId of newOrganizationIds) {
          organizationAccessData.push({
            organization_id: organizationId,
            level_type: "item",
            level_ids: [itemId],
            access_mode: newAccessMode,
            is_wildcard: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            created_by: user.id,
            modified_by: user.id,
            action: "add",
          });
        }
      } else {
        // Same access mode - handle additions and removals
        const addedOrganizations = newOrganizationIds.filter(
          (id: string) => !originalOrganizationIds.includes(id)
        );
        const removedOrganizations = originalOrganizationIds.filter(
          (id: string) => !newOrganizationIds.includes(id)
        );

        for (const organizationId of addedOrganizations) {
          organizationAccessData.push({
            organization_id: organizationId,
            level_type: "item",
            level_ids: [itemId],
            access_mode: newAccessMode,
            is_wildcard: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            created_by: user.id,
            modified_by: user.id,
            action: "add",
          });
        }

        for (const organizationId of removedOrganizations) {
          organizationAccessData.push({
            organization_id: organizationId,
            level_type: "item",
            level_ids: [itemId],
            access_mode: newAccessMode,
            is_wildcard: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            created_by: user.id,
            modified_by: user.id,
            action: "remove",
          });
        }
      }

      if (organizationAccessData.length > 0) {
        try {
          await apiClientDrawerV2.post(
            `/admin/organization-access?user_id=${user.id}`,
            {
              data: organizationAccessData,
            }
          );
        } catch (organizationAccessError) {
          console.error(
            "Error updating organization access:",
            organizationAccessError
          );
          Toastify("warning", "Organization access update failed");
        }
      }
    } else {
      // Handle organization access for new item
      const excludedOrganizations = formData.organizations_exclude
        .filter((id: number) => id < 0)
        .map((id: number) => Math.abs(id));
      const includedOrganizations = formData.organizations_exclude.filter(
        (id: number) => id > 0
      );

      let accessMode: "INCLUDE" | "EXCLUDE" = "INCLUDE";
      let organizationIds: string[] = [];

      if (excludedOrganizations.length > 0) {
        accessMode = "EXCLUDE";
        organizationIds = excludedOrganizations.map((id: number) =>
          id.toString()
        );
      } else if (includedOrganizations.length > 0) {
        accessMode = "INCLUDE";
        organizationIds = includedOrganizations.map((id: number) =>
          id.toString()
        );
      }

      if (organizationIds.length > 0) {
        const organizationAccessData = organizationIds.map(
          (organizationId: string) => ({
            organization_id: organizationId,
            level_type: "item",
            level_ids: [itemId],
            access_mode: accessMode,
            is_wildcard: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            created_by: user.id,
            modified_by: user.id,
            action: "add",
          })
        );

        try {
          await apiClientDrawerV2.post(
            `/admin/organization-access?user_id=${user.id}`,
            {
              data: organizationAccessData,
            }
          );
        } catch (organizationAccessError) {
          console.error(
            "Error creating organization access:",
            organizationAccessError
          );
          Toastify("warning", "Organization access creation failed");
        }
      }
    }
  };

  const handleAddDrawerItem = () => handleUpsertItem(false);

  const pascalCaseRegex = /^[A-Z][a-zA-Z0-9]*$/;
  const kebabCaseRegex = /^[a-z0-9]+(-[a-z0-9]+)*$/;

  const validateForm = (formData: any) => {
    let isValid = true;

    // Name validation
    if (!formData.display_name) {
      Toastify("error", "Please enter a display name");
      isValid = false;
    }

    // Route endpoint validation (kebab-case)
    if (formData.route_endpoint) {
      // if (!kebabCaseRegex.test(formData.route_endpoint)) {
      //   Toastify(
      //     "error",
      //     "Route endpoint must be in kebab-case format (e.g., my-route)"
      //   );
      //   isValid = false;
      // }
    } else {
      Toastify("error", "Please enter a route endpoint");
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = () => {
    if (!validateForm(formState)) {
      return;
    }

    // Additional required field validations
    if (!formState.selectedApplication) {
      Toastify("error", "Please select an application");
      return;
    }

    if (!formState.selectedChannel) {
      Toastify("error", "Please select a channel");
      return;
    }

    if (!formState.selectedUIComponents) {
      Toastify("error", "Please select a UI component");
      return;
    }

    if (
      formState.levelAccess === "Brand" &&
      formState.selectedBrands.length === 0
    ) {
      Toastify(
        "error",
        "Please select at least one brand for Brand level access"
      );
      return;
    }

    if (
      formState.levelAccess === "Organization" &&
      formState.selectedOrganizations.length === 0
    ) {
      Toastify(
        "error",
        "Please select at least one organization for Organization level access"
      );
      return;
    }

    handleAddDrawerItem();
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setFormState(initialFormState);
    setExisting(null);
    setMakeItTopAdd(false);
    setMakeItBelowAdd("");
    setSetAsDefaultAdd(false);
  };

  const handleEditDialogClose = () => {
    setOpenEditDialog(false);
    setFormStateEdit(initialFormStateEdit);
    setExisting(null);
    setCurrentData(null);
    setMakeItTopEdit(false);
    setMakeItBelowEdit("");
    setSetAsDefaultEdit(false);
  };

  const transformData = (data: any) => {
    const result: Record<string, string> = {};

    // Handle different data formats
    if (!data) {
      return result;
    }

    // If data is already an object (already transformed), return as is
    if (typeof data === 'object' && !Array.isArray(data)) {
      return data;
    }

    // If data is an array, process it (like v1)
    if (Array.isArray(data)) {
      data.forEach((section: any) => {
        const prefix = `ds${section.number}`;
        section.items.forEach((item: any) => {
          result[`${prefix}.${item.report}`] = item.hash;
        });
      });
    }

    return result;
  };

  const handleEditOpen = (itemData: any) => {
    // Set currentData for brand and organization access logic to work
    setCurrentData(itemData);

    // Extract just the endpoint name from the full route
    const routeEndPointLen = itemData.route_endpoint.split("/").length;
    const routeEndPoint =
      itemData.route_endpoint.split("/")[routeEndPointLen - 1];

    // Handle brand access data
    let brands_exclude: number[] = [];
    if (itemData.access?.brand_level) {
      const brandLevel = itemData.access.brand_level;
      if (brandLevel.brands && Array.isArray(brandLevel.brands)) {
        if (brandLevel.access_mode === "EXCLUDE") {
          brands_exclude = brandLevel.brands.map(
            (brand: any) => -parseInt(brand.id)
          );
        } else if (brandLevel.access_mode === "INCLUDE") {
          brands_exclude = brandLevel.brands.map((brand: any) =>
            parseInt(brand.id)
          );
        }
      }
    }

    // Handle organization access data
    let organizations_exclude: number[] = [];
    if (itemData.access?.organization_level) {
      const orgLevel = itemData.access.organization_level;
      if (orgLevel.organizations && Array.isArray(orgLevel.organizations)) {
        if (orgLevel.access_mode === "EXCLUDE") {
          organizations_exclude = orgLevel.organizations.map(
            (org: any) => -parseInt(org.id)
          );
        } else if (orgLevel.access_mode === "INCLUDE") {
          organizations_exclude = orgLevel.organizations.map((org: any) =>
            parseInt(org.id)
          );
        }
      }
    }

    const formStateEditFinal: FormStateEdit = {
      id: itemData.id,
      display_name: itemData.display_name,
      route_endpoint: routeEndPoint,
      params: itemData.params,
      icon: itemData.icon,
      selectedApplication: (() => {
        const selectedChannel = channels.find(
          (ch) => ch.id === itemData.channel_id
        );
        if (selectedChannel && applications) {
          return (
            applications.find(
              (app) => app.id === selectedChannel.application_id
            ) || null
          );
        }
        return null;
      })(),
      selectedChannel:
        channels.find((ch) => ch.id === itemData.channel_id) || null,
      selectedGroupings:
        groupings.find((g) => g.id === itemData.grouping_id) || null,
      selectedUIComponents:
        component.find((c) => c.id === itemData.ui_component_id) || null,
      selectedCustomComponents: (() => {
        const selectedCustomComponents = itemData.custom_component_config && Array.isArray(itemData.custom_component_config)
          ? itemData.custom_component_config
            .map((id: any) => {
              const foundComponent = customComponents.find(
                (component) => component.id === id
              );
              return foundComponent;
            })
            .filter(Boolean)
          : [];
        return selectedCustomComponents;
      })(),
      selectedReports: (() => {
        const selectedReports = itemData.report_config && Array.isArray(itemData.report_config)
          ? itemData.report_config
            .map((id: any) => {
              const foundReport = reports.find((report) => report.id === id);
              return foundReport;
            })
            .filter(Boolean)
          : [];
        return selectedReports;
      })(),
      levelAccess: itemData.level_access || "All",
      selectedBrands:
        itemData.level_access === "Brand" && itemData.level_id
          ? brands.filter((b) => itemData.level_id.includes(b.id))
          : [],
      selectedOrganizations:
        itemData.level_access === "Organization" && itemData.level_id
          ? organizations.filter((o) => itemData.level_id.includes(o.id))
          : [],
      selectedLevelId: itemData.level_id || [],
      report_type: itemData.report_type,
      isTeam: itemData.is_team || false,
      isRestricted: itemData.is_restricted || false,
      closeSidebarOnNavigate:
        itemData.extra_metadata?.close_sidebar_on_navigate ??
        itemData.close_sidebar_on_navigate ??
        false,
      selectedSandbox: (() => {
        // Check if any of the reports in report_config are sandbox reports
        if (itemData.report_config && Array.isArray(itemData.report_config)) {
          for (const reportId of itemData.report_config) {
            const sandboxReport = reportsSandbox.find((r) => r.id === reportId);
            if (sandboxReport) {
              return sandboxReport;
            }
          }
        }
        // Fallback to sandbox_id if no sandbox report found in report_config
        return itemData.sandbox_id && reportsSandbox
          ? reportsSandbox.find((r) => r.id === itemData.sandbox_id) || null
          : null;
      })(),
      brands_exclude,
      organizations_exclude,
      is_active: itemData.is_active,
      report_config: itemData.report_config,
      custom_component_config: itemData.custom_component_config,
    };

    console.log('itemData', itemData);

    // Set initial display order state for edit dialog
    setMakeItTopEdit(itemData.display_order === 0);

    // If display_order is not 0, find the item that's currently above this one
    let initialMakeItBelowEdit = "";
    if (itemData.display_order !== 0) {
      // Determine the parent (grouping or channel)
      let allItemsInParent: any[] = [];

      if (itemData.grouping_id) {
        // Item belongs to a grouping
        const selectedGrouping = groupingsWithChildren.find((grouping: any) => grouping.id === itemData.grouping_id);
        if (selectedGrouping && selectedGrouping.children) {
          allItemsInParent = selectedGrouping.children;
        } else {
          // Fallback
          allItemsInParent = drawerItem.filter((item: any) => item.grouping_id === itemData.grouping_id);
        }
      } else {
        // Item belongs directly to a channel
        const selectedChannel = channelsWithChildren.find((channel: any) => channel.id === itemData.channel_id);
        if (selectedChannel && selectedChannel.children) {
          allItemsInParent = selectedChannel.children.filter((item: any) => !item.grouping_id);
        } else {
          // Fallback
          allItemsInParent = drawerItem.filter((item: any) =>
            item.channel_id === itemData.channel_id && !item.grouping_id
          );
        }
      }

      // Sort and find the item above
      allItemsInParent.sort((a: any, b: any) => (a.display_order || 0) - (b.display_order || 0));
      const currentItemIndex = allItemsInParent.findIndex((item: any) => item.id === itemData.id);

      if (currentItemIndex > 0) {
        const itemAbove = allItemsInParent[currentItemIndex - 1];
        initialMakeItBelowEdit = itemAbove.id;
      }
    }

    // Check if this item already has default sidebar items
    const hasDefaultItems = defaultItems.some(defaultItem =>
      defaultItem.item_id === itemData.id &&
      defaultItem.application_id === formStateEditFinal.selectedApplication?.id
    );

    // Use setTimeout to ensure the state updates after the form state is set
    setTimeout(() => {
      setMakeItBelowEdit(initialMakeItBelowEdit);
      setSetAsDefaultEdit(hasDefaultItems); // Set the switch based on existing defaults
    }, 0);

    setFormStateEdit(formStateEditFinal);
    setOpenEditDialog(true);
  };

  const createEndPoint = (state: any) => {
    let endPointPath = "";

    if (state.selectedApplication?.id) {
      if (state.selectedApplication?.name) {
        const applicationPath = state.selectedApplication.name
          .toLowerCase()
          .replace(/\s+/g, "-");
        endPointPath += `/${applicationPath}`;
      }
    }

    if (state.selectedChannel?.id) {
      if (state.selectedChannel?.name) {
        const channelPath = state.selectedChannel.name
          .toLowerCase()
          .replace(/\s+/g, "-");
        endPointPath += `/${channelPath}`;
      }
    }

    if (state.selectedGroupings) {
      endPointPath += `/${state.selectedGroupings.path ||
        state.selectedGroupings.name?.toLowerCase().replace(/\s+/g, "-")
        }`;
    }

    endPointPath += `/`;

    return endPointPath;
  };

  const handleEditSubmit = () => handleUpsertItem(true);

  const searchResults = (query: any) => {
    const filteredUsers = [...drawerItemBackUp];
    let results = [];
    for (let i = 0; i < filteredUsers.length; i++) {
      if (
        filteredUsers[i]["display_name"]
          ?.toLowerCase()
          .includes(query.toLowerCase()) ||
        filteredUsers[i]["route_endpoint"]
          ?.toLowerCase()
          .includes(query.toLowerCase())
      )
        results.push(filteredUsers[i]);
    }

    setDrawerItems(results);
    setSearchedResults(results.map((item: any) => item.id));
    setPage(0); // Reset to first page on search
  };

  useEffect(() => {
    searchResults(searchText);
  }, [searchText]);

  // Reset "Move to below" selection when channel or grouping changes in Add dialog
  useEffect(() => {
    setMakeItBelowAdd("");
  }, [formState.selectedChannel, formState.selectedGroupings]);

  // Reset "Move to below" selection when channel or grouping changes in Edit dialog
  useEffect(() => {
    setMakeItBelowEdit("");
  }, [formStateEdit.selectedChannel, formStateEdit.selectedGroupings]);

  // Reset "Move to below" when "Move to top" is enabled in Add dialog
  useEffect(() => {
    if (makeItTopAdd) {
      setMakeItBelowAdd("");
    }
  }, [makeItTopAdd]);

  // Reset "Move to below" when "Move to top" is enabled in Edit dialog
  useEffect(() => {
    if (makeItTopEdit) {
      setMakeItBelowEdit("");
    }
  }, [makeItTopEdit]);

  // Automatically select "Move to top" when no other items exist in Add dialog
  useEffect(() => {
    if (availableItemsForBelowAdd.length === 0 && !makeItTopAdd) {
      setMakeItTopAdd(true);
      setMakeItBelowAdd("");
    }
  }, [availableItemsForBelowAdd.length, makeItTopAdd]);

  // Automatically select "Move to top" when no other items exist in Edit dialog
  useEffect(() => {
    if (availableItemsForBelowEdit.length === 0 && !makeItTopEdit) {
      setMakeItTopEdit(true);
      setMakeItBelowEdit("");
    }
  }, [availableItemsForBelowEdit.length, makeItTopEdit]);

  const [defaultBrands, setDefaultBrands] = useState([]);

  // Function to handle default item creation/update/deletion
  const handleDefaultItemCreation = async (itemId: string, formData: any, shouldSetAsDefault: boolean) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      const params: any = {
        user_id: user.id,
        is_admin: user.is_admin || false,
        is_team: user.is_team || false,
      };

      if (user.brand_id !== undefined && user.brand_id !== null && user.brand_id !== "") {
        params.brand_id = user.brand_id;
      }

      // If shouldSetAsDefault is false, delete all existing default items for this item
      if (!shouldSetAsDefault) {
        const existingDefaults = defaultItems.filter(item =>
          item.item_id === itemId &&
          item.application_id === formData.selectedApplication.id &&
          !item.user_id // Only delete items where user_id is null
        );

        if (existingDefaults.length > 0) {
          const deletePromises = existingDefaults.map(defaultItem =>
            apiClientDrawerV2.delete(`/admin/default-items/${defaultItem.id}`, { params })
          );

          await Promise.all(deletePromises);
          Toastify('success', `Removed ${existingDefaults.length} default sidebar item(s)`);
        }
        return;
      }

      // Get all existing default items for this item
      const allExistingDefaults = defaultItems.filter(item =>
        item.item_id === itemId &&
        item.application_id === formData.selectedApplication.id &&
        !item.user_id // Only consider items where user_id is null
      );

      // Check if there are brand access conditions
      const hasBrandConditions = formData.brands_exclude && formData.brands_exclude.length > 0;
      const hasOrgConditions = formData.organizations_exclude && formData.organizations_exclude.length > 0;

      if (!hasBrandConditions && !hasOrgConditions) {
        // No conditions - create/update application-wide default item
        const existingAppWide = allExistingDefaults.find(item =>
          !item.brand_id && !item.organization_id
        );

        // If there are existing specific defaults, update the first one to be app-wide
        // and delete the rest
        if (!existingAppWide && allExistingDefaults.length > 0) {
          // Update first existing item to be application-wide
          const firstExisting = allExistingDefaults[0];
          const payload = {
            id: firstExisting.id,
            application_id: formData.selectedApplication.id,
            item_id: itemId,
            is_active: true,
            user_id: null,
            brand_id: null,
            organization_id: null,
          };

          await apiClientDrawerV2.post('/admin/default-items', payload, { params });

          // Delete the remaining existing defaults
          if (allExistingDefaults.length > 1) {
            const deletePromises = allExistingDefaults.slice(1).map(defaultItem =>
              apiClientDrawerV2.delete(`/admin/default-items/${defaultItem.id}`, { params })
            );
            await Promise.all(deletePromises);
          }

          Toastify('success', 'Updated to application-wide default sidebar item');
        } else {
          // Create or update existing app-wide default
          const payload = {
            id: existingAppWide ? existingAppWide.id : generateUUID(),
            application_id: formData.selectedApplication.id,
            item_id: itemId,
            is_active: true,
            user_id: null,
            brand_id: null,
            organization_id: null,
          };

          await apiClientDrawerV2.post('/admin/default-items', payload, { params });
          Toastify('success', `Default sidebar item ${existingAppWide ? 'updated' : 'created'} (application-wide)`);
        }

      } else {
        // Handle INCLUDE conditions
        const includedBrands = formData.brands_exclude?.filter((id: number) => id > 0) || [];
        const includedOrgs = formData.organizations_exclude?.filter((id: number) => id > 0) || [];

        const excludedBrands = formData.brands_exclude?.filter((id: number) => id < 0).map((id: number) => Math.abs(id)) || [];
        const excludedOrgs = formData.organizations_exclude?.filter((id: number) => id < 0).map((id: number) => Math.abs(id)) || [];

        if (includedBrands.length > 0 || includedOrgs.length > 0) {
          // Smart update logic for INCLUDE conditions
          const promises: Promise<any>[] = [];
          const requiredEntries = [
            ...includedBrands.map((brandId: number) => ({ type: 'brand', id: brandId })),
            ...includedOrgs.map((orgId: number) => ({ type: 'org', id: orgId }))
          ];

          // Update existing entries and create new ones as needed
          for (let i = 0; i < requiredEntries.length; i++) {
            const entry = requiredEntries[i];
            let existingDefault;

            if (entry.type === 'brand') {
              existingDefault = allExistingDefaults.find(item =>
                String(item.brand_id) === String(entry.id)
              );
            } else {
              existingDefault = allExistingDefaults.find(item =>
                String(item.organization_id) === String(entry.id)
              );
            }

            // If no specific existing default, use any available existing default for the first entry
            if (!existingDefault && i === 0 && allExistingDefaults.length > 0) {
              existingDefault = allExistingDefaults[0];
            }

            const payload = {
              id: existingDefault ? existingDefault.id : generateUUID(),
              application_id: formData.selectedApplication.id,
              item_id: itemId,
              is_active: true,
              user_id: null,
              brand_id: entry.type === 'brand' ? entry.id : null,
              organization_id: entry.type === 'org' ? entry.id : null,
            };

            promises.push(apiClientDrawerV2.post('/admin/default-items', payload, { params }));
          }

          // Delete any remaining existing defaults that are no longer needed
          const usedExistingIds = new Set();
          for (let i = 0; i < Math.min(requiredEntries.length, allExistingDefaults.length); i++) {
            const entry = requiredEntries[i];
            let existingDefault;

            if (entry.type === 'brand') {
              existingDefault = allExistingDefaults.find(item =>
                String(item.brand_id) === String(entry.id) && !usedExistingIds.has(item.id)
              );
            } else {
              existingDefault = allExistingDefaults.find(item =>
                String(item.organization_id) === String(entry.id) && !usedExistingIds.has(item.id)
              );
            }

            if (!existingDefault) {
              existingDefault = allExistingDefaults.find(item => !usedExistingIds.has(item.id));
            }

            if (existingDefault) {
              usedExistingIds.add(existingDefault.id);
            }
          }

          const unusedExistingDefaults = allExistingDefaults.filter(item => !usedExistingIds.has(item.id));
          if (unusedExistingDefaults.length > 0) {
            const deletePromises = unusedExistingDefaults.map(defaultItem =>
              apiClientDrawerV2.delete(`/admin/default-items/${defaultItem.id}`, { params })
            );
            promises.push(...deletePromises);
          }

          // Execute all promises
          await Promise.all(promises);

          const includedEntities = [];
          if (includedBrands.length > 0) {
            includedEntities.push(`${includedBrands.length} brand(s)`);
          }
          if (includedOrgs.length > 0) {
            includedEntities.push(`${includedOrgs.length} organization(s)`);
          }

          Toastify('success',
            `Default sidebar items updated for ${includedEntities.join(' and ')}`
          );
        }

        // Handle EXCLUDE conditions (create application-wide with warning)
        if (excludedBrands.length > 0 || excludedOrgs.length > 0) {
          const existingAppWide = allExistingDefaults.find(item =>
            !item.brand_id && !item.organization_id
          );

          // Update existing to app-wide or create new app-wide
          const itemToUpdate = existingAppWide || (allExistingDefaults.length > 0 ? allExistingDefaults[0] : null);

          const payload = {
            id: itemToUpdate ? itemToUpdate.id : generateUUID(),
            application_id: formData.selectedApplication.id,
            item_id: itemId,
            is_active: true,
            user_id: null,
            brand_id: null,
            organization_id: null,
          };

          const promises: Promise<any>[] = [
            apiClientDrawerV2.post('/admin/default-items', payload, { params })
          ];

          // Delete other existing defaults if we updated one to be app-wide
          if (itemToUpdate && allExistingDefaults.length > 1) {
            const othersToDelete = allExistingDefaults.filter(item => item.id !== itemToUpdate.id);
            const deletePromises = othersToDelete.map(defaultItem =>
              apiClientDrawerV2.delete(`/admin/default-items/${defaultItem.id}`, { params })
            );
            promises.push(...deletePromises);
          }

          await Promise.all(promises);

          // Show warning about excluded entities
          const excludedEntities = [];
          if (excludedBrands.length > 0) {
            excludedEntities.push(`${excludedBrands.length} brand(s)`);
          }
          if (excludedOrgs.length > 0) {
            excludedEntities.push(`${excludedOrgs.length} organization(s)`);
          }

          Toastify('warning',
            `Default item updated (application-wide) but has ${excludedEntities.join(' and ')} excluded. Please verify in Default Sidebar Items tab.`
          );
        }
      }

    } catch (error) {
      console.error('Error managing default items:', error);
      Toastify('error', 'A default sidebar item already exists for this application.');
    }
  };

  return (
    <>
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="flex flex-wrap items-end gap-3 mb-3 flex-shrink-0">
          <div className="space-y-1.5 min-w-[160px]">
            <Label htmlFor="filter-application-items" className="text-sm font-medium">
              Application
            </Label>
            <SingleSelect
              options={[
                { label: "All applications", value: "all" },
                ...availableApplicationOptions,
              ]}
              defaultValue={filterApplicationId || "all"}
              onChange={(value) => {
                setFilterApplicationId(!value || value === "all" ? "" : value);
                setFilterOrganizationId("");
                setFilterBrandId("");
              }}
              placeholder="All applications"
              containerClassName="w-full"
            />
          </div>
          <div className="space-y-1.5 min-w-[160px]">
            <div className="flex items-center gap-1">
              <Label htmlFor="filter-organization-items" className="text-sm font-medium">
                Organization
              </Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info
                      className="h-3.5 w-3.5 text-muted-foreground cursor-default"
                      aria-hidden="true"
                    />
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <span className="max-w-xs text-xs">
                      Filters drawer items by organizations that are included or excluded in their access settings.
                    </span>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <SingleSelect
              options={[
                { label: "All organizations", value: "all" },
                ...availableOrganizationOptions,
              ]}
              defaultValue={filterOrganizationId || "all"}
              onChange={(value) => {
                setFilterOrganizationId(!value || value === "all" ? "" : value);
                setFilterBrandId("");
              }}
              placeholder="All organizations"
              containerClassName="w-full"
            />
          </div>
          <div className="space-y-1.5 min-w-[160px]">
            <div className="flex items-center gap-1">
              <Label htmlFor="filter-brand-items" className="text-sm font-medium">
                Brand
              </Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info
                      className="h-3.5 w-3.5 text-muted-foreground cursor-default"
                      aria-hidden="true"
                    />
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <span className="max-w-xs text-xs">
                      Filters drawer items by brands that are included or excluded in their access settings.
                    </span>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <SingleSelect
              options={[
                { label: "All brands", value: "all" },
                ...availableBrandOptions,
              ]}
              defaultValue={filterBrandId || "all"}
              onChange={(value) => setFilterBrandId(!value || value === "all" ? "" : value)}
              placeholder="All brands"
              containerClassName="w-full"
            />
          </div>
          <div className="space-y-1.5 min-w-[180px] max-w-[280px] ml-auto">
            <Label htmlFor="columns-items" className="text-sm font-medium">
              Columns
            </Label>
            <MultiSelectCheckboxTag
              placeholder={selectedItemColumns.length > 0 ? `Columns (${selectedItemColumns.length})` : "Select Columns"}
              options={ITEMS_SELECTABLE_COLUMN_KEYS.map((key) => ({
                value: key,
                label: key === "display_name" ? "Drawer Item Name" : key === "organizations" ? "Excluded/Include Organizations" : key === "brands" ? "Excluded/Include Brands" : key === "created_at" ? "Created" : key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
              }))}
              value={selectedItemColumns}
              onChange={(ids: string[]) => ids.length > 0 && setSelectedItemColumns(ids)}
              containerClassName="w-full"
              showSelectAll
            />
          </div>
        </div>
        <DataTable
          className="flex-1 min-h-0 overflow-auto"
          maxHeight="100%"
            columns={(() => {
              const dataCols: TableColumn<any>[] = [
                {
                  key: "application",
                  header: "Application",
                  accessor: (row: any) => getApplicationName(row),
                  width: "180px",
                  render: (_v: any, row: any) => <span className="text-gray-600">{getApplicationName(row)}</span>,
                },
                {
                  key: "channel",
                  header: "Channel",
                  accessor: (row: any) => channelMapping[row.channel_id] || "",
                  width: "200px",
                },
                {
                  key: "grouping",
                  header: "Grouping",
                  accessor: (row: any) => row.grouping_id ? groupingsMapping[row.grouping_id] || "N/A" : "None",
                  width: "200px",
                },
                {
                  key: "display_name",
                  header: "Drawer Item Name",
                  accessor: "display_name",
                  sortable: true,
                  width: "250px",
                  render: (_v: any, row: any) => <span className="font-medium">{row.display_name}</span>,
                },
                {
                  key: "reports",
                  header: "Reports",
                  accessor: (row: any) =>
                    Array.isArray(row?.report_config)
                      ? row.report_config.map((id: string) => reportsMapping[id]).filter(Boolean).join(", ")
                      : "",
                  width: "300px",
                },
                {
                  key: "organizations",
                  header: "Excluded/Include Organizations",
                  accessor: (row: any) => row.id,
                  width: "300px",
                  align: "center",
                  render: (_v: any, row: any) => <OrganizationCellRenderer data={row} organizationsList={filterOrganizations.length ? filterOrganizations : organizations} />,
                },
                {
                  key: "brands",
                  header: "Excluded/Include Brands",
                  accessor: (row: any) => row.id,
                  width: "300px",
                  align: "center",
                  render: (_v: any, row: any) => <BrandCellRenderer data={row} brandsList={filterBrands.length ? filterBrands : brands} />,
                },
                {
                  key: "status",
                  header: "Status",
                  accessor: "is_active",
                  width: "120px",
                  render: (_v: any, row: any) => (
                    <Badge variant={row.is_active ? "default" : "secondary"}>
                      {row.is_active ? "Active" : "Inactive"}
                    </Badge>
                  ),
                },
                {
                  key: "ui_component",
                  header: "UI Component",
                  accessor: (row: any) => componentMapping[row?.ui_component_id] || "",
                  width: "200px",
                },
                {
                  key: "custom_components",
                  header: "Custom Components",
                  accessor: (row: any) =>
                    Array.isArray(row?.custom_component_config)
                      ? row.custom_component_config.map((id: string) => customComponentsMapping[id]).filter(Boolean).join(", ")
                      : "",
                  width: "300px",
                },
                {
                  key: "route_endpoint",
                  header: "Route Endpoint",
                  accessor: "route_endpoint",
                  width: "250px",
                },
                {
                  key: "params",
                  header: "Params",
                  accessor: (row: any) =>
                    row.params
                      ? typeof row.params === "object"
                        ? JSON.stringify(row.params)
                        : String(row.params)
                      : "",
                  width: "200px",
                  render: (_v: any, row: any) => {
                    const text = row.params
                      ? typeof row.params === "object"
                        ? JSON.stringify(row.params)
                        : String(row.params)
                      : "";
                    return (
                      <span className="block truncate max-w-[200px]" title={text}>
                        {text || "—"}
                      </span>
                    );
                  },
                },
                {
                  key: "created_at",
                  header: "Created",
                  accessor: (row: any) => row.created_at ? new Date(row.created_at).toLocaleDateString() : "",
                  width: "150px",
                },
              ];
              const filtered = ITEMS_SELECTABLE_COLUMN_KEYS.filter((k) => selectedItemColumns.includes(k))
                .map((k) => dataCols.find((c) => c.key === k))
                .filter(Boolean) as TableColumn<any>[];
              if (showActionsColumn) {
                filtered.push({
                  key: "actions",
                  header: "Actions",
                  accessor: (row: any) => row.id,
                  width: "150px",
                  align: "center",
                  render: (_v: any, row: any) => (
                    <div className="flex gap-1 justify-center">
                      {hasEditAccess && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            handleEditOpen(row);
                            setCurrentData(row);
                          }}
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      )}
                      {hasManageAccess && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setOpenDeleteDialog(true);
                            setCurrentData(row);
                          }}
                          className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ),
                });
              }
              return filtered;
            })()}
            data={paginatedData}
            loading={loadData}
            emptyMessage="No drawer items found."
            stickyHeader
            stickyFooter
            showActions={false}
            pagination={{
              currentPage: page + 1,
              totalPages: Math.max(1, totalPages),
              pageSize: rowsPerPage,
              totalItems: filteredData.length,
              onPageChange: (p) => handlePageChange(p - 1),
              onPageSizeChange: (size) => handleRowsPerPageChange(String(size)),
              pageSizeOptions: [10, 20, 50, 100],
            }}
          />
        </div>
        <Dialog open={openDeleteDialog} onOpenChange={setOpenDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Are you sure you want to delete?</DialogTitle>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setOpenDeleteDialog(false);
                  setCurrentData(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => handleDelete(currentData)}
                disabled={loadData}
              >
                {loadData ? (
                  <>
                    <SpinnerLoader className="mr-2 h-4 w-4" />
                    Deleting...
                  </>
                ) : (
                  "Delete"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Sheet open={openDialog} onOpenChange={(open: boolean) => !open && handleDialogClose()}>
          <SheetContent
            side="right"
            className="w-full sm:max-w-2xl flex flex-col h-full"
            onPointerDownOutside={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
          >
            <SheetHeader>
              <SheetTitle>Add New Item</SheetTitle>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto min-h-0">
            <div className="py-4 px-1">
            {formState.report_type === "Default" && (
              <>
                <div className="flex items-center justify-between gap-4 mt-4 mb-4">
                  <h2 className="text-lg font-semibold m-0">Manage Access</h2>
                  <div className="flex items-center gap-4 flex-shrink-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">Restricted</span>
                      <Switch
                        checked={formState.isRestricted}
                        onCheckedChange={(checked: boolean) =>
                          handleFormChange("isRestricted", checked)
                        }
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">Team Access</span>
                      <Switch
                        checked={formState.isTeam}
                        onCheckedChange={(checked: boolean) =>
                          handleFormChange("isTeam", checked)
                        }
                      />
                    </div>
                  </div>
                </div>

                <Collapsible className="mb-6">
                  <CollapsibleTrigger className="flex items-center justify-between w-full rounded-lg border border-input bg-background px-4 py-3 text-left font-medium hover:bg-accent/50 h-10 [&[data-state=open]>svg]:rotate-180">
                    <span>Brand Access</span>
                    <ChevronDown className="h-4 w-4 shrink-0 transition-transform" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-3">
                    <p className="text-sm text-muted-foreground flex items-center mb-3">
                      <Info className="h-4 w-4 shrink-0 mr-1" />
                      Use negative IDs (e.g., -1, -2) to exclude specific brands. Use positive IDs (e.g., 1, 2) to include only those specific brands.
                    </p>
                    <BrandMultiSelect
                      defaultBrands={formState.brands_exclude}
                      setDefaultBrands={(field: string, value: number[]) => {
                        handleFormChange("brands_exclude", value);
                      }}
                    />
                  </CollapsibleContent>
                </Collapsible>

                <Collapsible className="mb-8">
                  <CollapsibleTrigger className="flex items-center justify-between w-full rounded-lg border border-input bg-background px-4 py-3 text-left font-medium hover:bg-accent/50 h-10 [&[data-state=open]>svg]:rotate-180">
                    <span>Organization Access</span>
                    <ChevronDown className="h-4 w-4 shrink-0 transition-transform" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-3">
                    <p className="text-sm text-muted-foreground flex items-center mb-3">
                      <Info className="h-4 w-4 shrink-0 mr-1" />
                      Use negative IDs (e.g., -1, -2) to exclude specific organizations. Use positive IDs (e.g., 1, 2) to include only those specific organizations.
                    </p>
                    <OrganizationMultiSelect
                      defaultOrganizations={formState.organizations_exclude}
                      setDefaultOrganizations={(field: string, value: number[]) => {
                        handleFormChange("organizations_exclude", value);
                      }}
                    />
                  </CollapsibleContent>
                </Collapsible>
              </>
            )}

            <div className="mb-4 hidden">
              <Label className="mb-2 block">Report Type</Label>
              <SingleSelect
                options={[
                  { label: "Default", value: "Default" },
                ]}
                defaultValue={formState.report_type || "Default"}
                onChange={(value) => {
                  const next = value || "Default";
                  handleFormChange("report_type", next);
                  handleFormChange("levelAccess", next === "Default" ? "All" : "Brand");
                }}
                placeholder="Report Type"
                containerClassName="w-full"
              />
            </div>

            <div className="mb-4">
              <Label className="mb-2 block">Display Name *</Label>
              <Input
                value={formState.display_name || ""}
onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                handleFormChange("display_name", e.target.value);
                handleFormChange(
                  "route_endpoint",
                  e.target.value.toLowerCase().replace(/\s+/g, "-")
                );
              }}
                className="w-full"
                placeholder="Display Name"
              />
            </div>

            <div className="mb-4">
              <Label className="mb-2 block">Select Level Access</Label>
              <SingleSelect
                options={
                  formState.report_type === "Default"
                    ? [{ label: "All", value: "All" }]
                    : [
                        { label: "Brand", value: "Brand" },
                        { label: "Organization", value: "Organization" },
                      ]
                }
                defaultValue={formState.levelAccess || (formState.report_type === "Default" ? "All" : "Brand")}
                onChange={(value) =>
                  handleFormChange(
                    "levelAccess",
                    value || (formState.report_type === "Default" ? "All" : "Brand"),
                  )
                }
                placeholder="Select Level Access"
                containerClassName="w-full"
              />
            </div>

            {formState.levelAccess === "Brand" && (
              <div className="mb-4">
                <Label className="mb-2 block">Select Level Access Brands</Label>
                <MultiSelectCheckboxTag
                  placeholder="Select Level Access Brands"
                  options={brands.map((b) => ({ value: String(b.id), label: b.name }))}
                  value={formState.selectedBrands.map((b) => String(b.id))}
                  onChange={(ids: string[]) =>
                    handleFormChange(
                      "selectedBrands",
                      ids.map((id: string) => brands.find((b) => String(b.id) === id)).filter(Boolean) as any[]
                    )
                  }
                  containerClassName="w-full"
                  showSelectAll
                />
              </div>
            )}

            {formState.levelAccess === "Organization" && (
              <div className="mb-4">
                <Label className="mb-2 block">Select Level Access Organizations</Label>
                <MultiSelectCheckboxTag
                  placeholder="Select Level Access Organizations"
                  options={organizations.map((o) => ({ value: String(o.id), label: o.name }))}
                  value={formState.selectedOrganizations.map((o) => String(o.id))}
                  onChange={(ids: string[]) =>
                    handleFormChange(
                      "selectedOrganizations",
                      ids.map((id: string) => organizations.find((o) => String(o.id) === id)).filter(Boolean) as any[]
                    )
                  }
                  containerClassName="w-full"
                  showSelectAll
                />
              </div>
            )}

            <div className="mb-4">
              <Label className="mb-2 block">Select Application *</Label>
              <SingleSelect
                options={(applications || []).map((a) => ({
                  label: (a as any).display_name || a.name,
                  value: String(a.id),
                }))}
                defaultValue={formState.selectedApplication ? String(formState.selectedApplication.id) : undefined}
                onChange={(value) => {
                  const app = applications?.find((a) => String(a.id) === value);
                  handleFormChange("selectedApplication", app ?? null);
                  handleFormChange("selectedChannel", null);
                  handleFormChange("selectedGroupings", null);
                }}
                placeholder="Select Application *"
                containerClassName="w-full"
              />
            </div>

            <div className="mb-4">
              <Label className="mb-2 block">Select Channel *</Label>
              <SingleSelect
                options={channels
                  .filter((ch) =>
                    formState.selectedApplication
                      ? ch.application_id === formState.selectedApplication.id
                      : true
                  )
                  .map((ch) => {
                    const appForChannel = (applications || []).find(
                      (a: any) => String(a.id) === String(ch.application_id),
                    );
                    const appName =
                      (appForChannel as any)?.display_name || appForChannel?.name;
                    const name = ch.name;
                    return {
                      label: appName ? `${name} (${appName})` : name,
                      value: String(ch.id),
                    };
                  })}
                defaultValue={formState.selectedChannel ? String(formState.selectedChannel.id) : undefined}
                onChange={(value) => {
                  const ch = channels.find((c) => String(c.id) === value);
                  handleFormChange("selectedChannel", ch ?? null);
                }}
                placeholder="Select Channel *"
                containerClassName="w-full"
              />
            </div>

            <div className="mb-4">
              <Label className="mb-2 block">Select Grouping (Optional)</Label>
              <SingleSelect
                options={groupings
                  .filter((g) =>
                    formState.selectedChannel
                      ? g.channel_id === formState.selectedChannel.id
                      : true
                  )
                  .map((g) => ({
                    label: `${g.name || "Unnamed"} (${channelMapping[g.channel_id] || "Unknown Channel"})`,
                    value: String(g.id),
                  }))}
                defaultValue={formState.selectedGroupings ? String(formState.selectedGroupings.id) : undefined}
                onChange={(value) => {
                  const grp = groupings.find((g) => String(g.id) === value);
                  handleFormChange("selectedGroupings", grp ?? null);
                }}
                placeholder="Select Grouping (Optional)"
                containerClassName="w-full"
              />
            </div>

            <h2 className="text-lg font-semibold mt-6 mb-1">Display Order</h2>

            <p className="text-sm text-muted-foreground flex items-center mb-4">
              <Info className="h-4 w-4 shrink-0 mr-1" />
              Control the position of this item within the selected {formState.selectedGroupings ? 'grouping' : 'channel'}. Use "Move to top" to place at the top, or select an item in "Move to below" to place under it. Order is 0 based indexing.
            </p>

            <div className="flex items-center gap-2 mb-4">
              <Label htmlFor="make-it-top-add">Move to top</Label>
              <Switch
                id="make-it-top-add"
                checked={makeItTopAdd}
                onCheckedChange={setMakeItTopAdd}
              />
            </div>

            {makeItTopAdd && availableItemsForBelowAdd.length === 0 && (
              <p className="text-sm text-muted-foreground flex items-center mb-4">
                <Info className="h-4 w-4 shrink-0 mr-1" />
                No other items are present in this {formState.selectedGroupings ? 'grouping' : 'channel'}.
              </p>
            )}

            {!makeItTopAdd && (
              <div className="mb-4">
                <Label className="mb-2 block">Move to below *</Label>
                <SingleSelect
                  options={
                    availableItemsForBelowAdd.length === 0
                      ? []
                      : availableItemsForBelowAdd.map((item: any) => {
                          const fullItemData = drawerItem.find((di: any) => di.id === item.id) || item;
                          let accessInfo = "";
                          let hasBrandAccess = false;
                          if (fullItemData.access?.brand_level?.brands?.length > 0) {
                            const brandMode = fullItemData.access.brand_level.access_mode;
                            const brandNames = fullItemData.access.brand_level.brands
                              .map((brand: any) => brand.name)
                              .join(", ");
                            accessInfo += `Brand ${brandMode}: ${brandNames}`;
                            hasBrandAccess = true;
                          }
                          if (fullItemData.access?.organization_level?.organizations?.length > 0) {
                            const orgMode = fullItemData.access.organization_level.access_mode;
                            const orgNames = fullItemData.access.organization_level.organizations
                              .map((org: any) => org.name)
                              .join(", ");
                            if (hasBrandAccess) accessInfo += " | ";
                            accessInfo += `Org ${orgMode}: ${orgNames}`;
                          }
                          return {
                            label: accessInfo
                              ? `${item.display_name || item.name} — ${accessInfo}`
                              : item.display_name || item.name,
                            value: item.id,
                          };
                        })
                  }
                  defaultValue={makeItBelowAdd || undefined}
                  onChange={(value) => value && setMakeItBelowAdd(value)}
                  placeholder={
                    availableItemsForBelowAdd.length === 0
                      ? `No other items available in this ${formState.selectedGroupings ? "grouping" : "channel"}`
                      : "Select item"
                  }
                  containerClassName="w-full"
                  disabled={availableItemsForBelowAdd.length === 0}
                />
              </div>
            )}

            <p className="text-sm text-muted-foreground mb-4">
              {makeItTopAdd ? (
                <><strong>Current Position:</strong> &nbsp;<span>Top (Order: 0)</span></>
              ) : makeItBelowAdd ? (
                (() => {
                  const selectedItemIndex = availableItemsForBelowAdd.findIndex((item: any) => item.id === makeItBelowAdd);
                  const selectedItem = availableItemsForBelowAdd.find((item: any) => item.id === makeItBelowAdd);
                  const itemName = selectedItem ? (selectedItem.display_name || selectedItem.name) : makeItBelowAdd;
                  const newOrder = selectedItemIndex !== -1 ? selectedItemIndex + 1 : 0;
                  return <><strong>Current Position:</strong> &nbsp;<span>Below &quot;{itemName}&quot; (Order: {newOrder})</span></>;
                })()
              ) : (
                <><strong>Current Position:</strong> &nbsp;<span>Not Set</span></>
              )}
            </p>

            <Separator className="my-4" />

            <div className="mb-4">
              <Label className="mb-2 block">Select UI Component *</Label>
              <SingleSelect
                options={component.map((c) => ({
                  label: c.name,
                  value: String(c.id),
                }))}
                defaultValue={formState.selectedUIComponents ? String(formState.selectedUIComponents.id) : undefined}
                onChange={(value) => {
                  const comp = component.find((c) => String(c.id) === value);
                  handleFormChange("selectedUIComponents", comp ?? null);
                }}
                placeholder="Select UI Component *"
                containerClassName="w-full"
              />
            </div>

            <div className="mb-4">
              <Label className="mb-2 block">Select Custom Component Name</Label>
              <MultiSelectCheckboxTag
                placeholder="Select Custom Component Name"
                options={(formState.selectedUIComponents?.name === "SuperComponent" ? customComponents : customComponents.filter((option: any) => option.name === 'All Brand')).map((c: any) => ({ value: String(c.id), label: c.name }))}
                value={formState.selectedCustomComponents.map((c) => String(c.id))}
                onChange={(ids: string[]) =>
                  handleFormChange(
                    "selectedCustomComponents",
                    ids.map((id: string) => customComponents.find((c: any) => String(c.id) === id)).filter(Boolean) as any[]
                  )
                }
                containerClassName="w-full"
                showSelectAll
              />
            </div>

            <div className="flex items-center gap-2 mt-2 mb-2">
              <Info className="h-4 w-4 shrink-0 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">If you want multiple reports, contact support.</p>
            </div>

            <div
              className={`mb-4 rounded-md ${formState.selectedUIComponents?.component_type === 2 ? 'pointer-events-none opacity-50 grayscale' : ''}`}
            >
              <Label className="mb-2 block">Select Reports (Optional)</Label>
              <MultiSelectCheckboxTag
                placeholder="Select reports"
                options={reports.map((r: any) => ({ label: r.display_name || String(r.id), value: String(r.id) }))}
                value={(formState.selectedReports || []).map((r: any) => String(r.id))}
                onChange={(selectedIds: string[]) =>
                  handleFormChange(
                    "selectedReports",
                    selectedIds.map((id) => reports.find((r: any) => String(r.id) === id)).filter(Boolean) as any[]
                  )
                }
                containerClassName="w-full"
                showSelectAll
              />
            </div>

            <div
              className={`mb-4 rounded-md ${formState.selectedUIComponents?.component_type === 2 ? 'pointer-events-none opacity-50 grayscale' : ''}`}
            >
              <Label className="mb-2 block">Select Sandbox</Label>
              <SingleSelect
                options={reportsSandbox.map((s) => ({
                  label: s.display_name,
                  value: String(s.id),
                }))}
                defaultValue={formState.selectedSandbox ? String(formState.selectedSandbox.id) : undefined}
                onChange={(value) => {
                  const sandbox = reportsSandbox.find((s) => String(s.id) === value);
                  handleFormChange("selectedSandbox", sandbox ?? null);
                }}
                placeholder="Select Sandbox"
                containerClassName="w-full"
              />
            </div>

            {formState.selectedUIComponents?.component_type === 2 && (
              <p className="text-xs text-muted-foreground -mt-2 mb-4">
                Only <strong>Looker studio Reports</strong> and <strong>Tableau</strong> UI components can use Reports/Sandbox options.
              </p>
            )}

            <div className="mb-4">
              <Label className="mb-2 block">Route Endpoint</Label>
              <div className="flex rounded-lg border border-input bg-background overflow-hidden">
                <span className="flex items-center px-3 text-sm text-muted-foreground border-r border-input bg-muted/30">
                  {createEndPoint(formState)}
                </span>
                <Input
                  value={formState.route_endpoint || ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFormChange("route_endpoint", e.target.value)}
                  className="rounded-none border-0 focus-visible:ring-0"
                  placeholder="my-route"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">Use kebab-case format (e.g., my-route)</p>
            </div>

            <div className="mb-4">
              <Label className="mb-2 block">Icon</Label>
              <IconSelect
                options={iconOptions}
                value={formState.icon || null}
                onChange={(val) => handleFormChange("icon", val)}
                placeholder="Icon"
              />
            </div>

            <div className="flex items-center gap-2 mb-4">
              <Label htmlFor="active-add">Active:</Label>
              <Switch
                id="active-add"
                checked={formState.is_active}
                onCheckedChange={(checked: boolean) => handleFormChange("is_active", checked)}
              />
            </div>

            <div className="flex items-center gap-2 mb-4">
              <Label htmlFor="close-sidebar-add">Close sidebar after opening:</Label>
              <Switch
                id="close-sidebar-add"
                checked={formState.closeSidebarOnNavigate}
                onCheckedChange={(checked: boolean) => handleFormChange("closeSidebarOnNavigate", checked)}
              />
              <span className="text-sm text-muted-foreground ml-1">Collapse the sidebar once this item is opened</span>
            </div>

            <div className="flex items-center gap-2 mb-2">
              <Label htmlFor="set-default-add">Set as Default:</Label>
              <Switch
                id="set-default-add"
                checked={setAsDefaultAdd}
                onCheckedChange={setSetAsDefaultAdd}
              />
              <span className="text-sm text-muted-foreground ml-1">Automatically create default sidebar item</span>
            </div>

            {setAsDefaultAdd && (
              <div className="mb-4 p-3 rounded-lg bg-muted/50 text-sm">
                {(() => {
                  const includedBrands = formState.brands_exclude?.filter((id: number) => id > 0) || [];
                  const includedOrgs = formState.organizations_exclude?.filter((id: number) => id > 0) || [];
                  const excludedBrands = formState.brands_exclude?.filter((id: number) => id < 0) || [];
                  const excludedOrgs = formState.organizations_exclude?.filter((id: number) => id < 0) || [];

                  if (includedBrands.length > 0 || includedOrgs.length > 0) {
                    const includedEntities = [];
                    if (includedBrands.length > 0) includedEntities.push(`${includedBrands.length} brand(s)`);
                    if (includedOrgs.length > 0) includedEntities.push(`${includedOrgs.length} organization(s)`);
                    return (
                      <p className="flex items-center">
                        <Info className="h-4 w-4 shrink-0 mr-2" />
                        This item has INCLUDE access conditions. Default items will be created only for {includedEntities.join(' and ')}.
                      </p>
                    );
                  } else if (excludedBrands.length > 0 || excludedOrgs.length > 0) {
                    const excludedEntities = [];
                    if (excludedBrands.length > 0) excludedEntities.push(`${excludedBrands.length} brand(s)`);
                    if (excludedOrgs.length > 0) excludedEntities.push(`${excludedOrgs.length} organization(s)`);
                    return (
                      <p className="flex items-center">
                        <Info className="h-4 w-4 shrink-0 mr-2" />
                        This item has EXCLUDE access conditions for {excludedEntities.join(' and ')}. An application-wide default will be created.
                      </p>
                    );
                  } else {
                    return (
                      <p className="flex items-center">
                        <Info className="h-4 w-4 shrink-0 mr-2" />
                        No access conditions detected. An application-wide default item will be created.
                      </p>
                    );
                  }
                })()}
              </div>
            )}

            {formState.selectedUIComponents?.name !== "SuperComponent" ? (
              <div className="mb-4">
                <Label className="mb-2 block">Params</Label>
                <Input
                  value={formState.params || ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFormChange("params", e.target.value)}
                  placeholder="1,2,3"
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground mt-1">Enter comma-separated numbers (e.g., 1,2,3)</p>
              </div>
            ) : (
              <DynamicNestedForm
                formStateEdit={formState}
                handleFormEditChange={handleFormChange}
              />
            )}
            </div>
            </div>
            <SheetFooter className="gap-2 pt-4 border-t flex-shrink-0 mt-auto">
              <Button variant="outline" onClick={handleDialogClose}>
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={handleSubmit}
                disabled={
                  !formState.display_name?.trim() ||
                  !formState.selectedApplication ||
                  !formState.selectedChannel ||
                  !formState.selectedUIComponents ||
                  (!makeItTopAdd && !makeItBelowAdd) ||
                  loadData
                }
              >
                {loadData ? "Adding..." : "Add Item"}
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>

        <Sheet open={openEditDialog} onOpenChange={(open: boolean) => !open && handleEditDialogClose()}>
          <SheetContent
            side="right"
            className="sm:max-w-2xl flex flex-col h-full"
            onPointerDownOutside={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
          >
            <SheetHeader className="flex-shrink-0">
              <SheetTitle>Edit Item</SheetTitle>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto min-h-0 py-4 px-1">
            <div className="flex items-center justify-between gap-4 mb-4">
              <h2 className="text-lg font-semibold m-0">Manage Access</h2>
              <div className="flex items-center gap-4 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <Label htmlFor="edit-restricted" className="text-sm font-medium">Restricted</Label>
                  <Switch
                    id="edit-restricted"
                    checked={formStateEdit.isRestricted}
                    onCheckedChange={(checked: boolean) => handleFormEditChange("isRestricted", checked)}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="edit-team-access" className="text-sm font-medium">Team Access</Label>
                  <Switch
                    id="edit-team-access"
                    checked={formStateEdit.isTeam}
                    onCheckedChange={(checked: boolean) => handleFormEditChange("isTeam", checked)}
                  />
                </div>
              </div>
            </div>
            {formStateEdit.report_type === "Default" && (
              <>
                <Collapsible className="mb-6">
                  <CollapsibleTrigger className="flex items-center justify-between w-full rounded-lg border border-input bg-background px-4 py-3 text-left font-medium hover:bg-accent/50 h-10 [&[data-state=open]>svg]:rotate-180">
                    <span>Brand Access</span>
                    <ChevronDown className="h-4 w-4 shrink-0 transition-transform" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-3">
                    <p className="text-sm text-muted-foreground flex items-center mb-3">
                      <Info className="h-4 w-4 shrink-0 mr-1" />
                      Use negative IDs (e.g., -1, -2) to exclude specific brands. Use positive IDs (e.g., 1, 2) to include only those specific brands.
                    </p>
                    <BrandMultiSelect
                      defaultBrands={formStateEdit.brands_exclude}
                      setDefaultBrands={(field: string, value: number[]) => handleFormEditChange("brands_exclude", value)}
                    />
                  </CollapsibleContent>
                </Collapsible>
                <Collapsible className="mb-8">
                  <CollapsibleTrigger className="flex items-center justify-between w-full rounded-lg border border-input bg-background px-4 py-3 text-left font-medium hover:bg-accent/50 h-10 [&[data-state=open]>svg]:rotate-180">
                    <span>Organization Access</span>
                    <ChevronDown className="h-4 w-4 shrink-0 transition-transform" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-3">
                    <p className="text-sm text-muted-foreground flex items-center mb-3">
                      <Info className="h-4 w-4 shrink-0 mr-1" />
                      Use negative IDs (e.g., -1, -2) to exclude specific organizations. Use positive IDs (e.g., 1, 2) to include only those specific organizations.
                    </p>
                    <OrganizationMultiSelect
                      defaultOrganizations={formStateEdit.organizations_exclude}
                      setDefaultOrganizations={(field: string, value: number[]) => handleFormEditChange("organizations_exclude", value)}
                    />
                  </CollapsibleContent>
                </Collapsible>
              </>
            )}

            <div className="mb-4 hidden">
              <Label className="mb-2 block">Report Type</Label>
              <SingleSelect
                options={[{ label: "Default", value: "Default" }]}
                defaultValue={formStateEdit.report_type || "Default"}
                onChange={(value) => {
                  const val = value || "Default";
                  handleFormEditChange("report_type", val);
                  handleFormEditChange("levelAccess", val === "Default" ? "All" : "Brand");
                }}
                placeholder="Report Type"
                containerClassName="w-full"
              />
            </div>

            <div className="mb-4">
              <Label className="mb-2 block">Display Name *</Label>
              <Input
                value={formStateEdit.display_name || ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  handleFormEditChange("display_name", e.target.value);
                  handleFormEditChange("route_endpoint", e.target.value.toLowerCase().replace(/\s+/g, "-"));
                }}
                className="w-full h-10"
                placeholder="Display Name"
              />
            </div>

            <div className="mb-4">
              <Label className="mb-2 block">Select Level Access</Label>
              <SingleSelect
                options={
                  formStateEdit.report_type === "Default"
                    ? [{ label: "All", value: "All" }]
                    : [
                        { label: "Brand", value: "Brand" },
                        { label: "Organization", value: "Organization" },
                      ]
                }
                defaultValue={
                  formStateEdit.levelAccess ||
                  (formStateEdit.report_type === "Default" ? "All" : "Brand")
                }
                onChange={(value) =>
                  handleFormEditChange(
                    "levelAccess",
                    value || (formStateEdit.report_type === "Default" ? "All" : "Brand"),
                  )
                }
                placeholder="Select Level Access"
                containerClassName="w-full"
              />
            </div>

            {formStateEdit.levelAccess === "Brand" && (
              <div className="mb-4">
                <Label className="mb-2 block">Select Level Access Brands</Label>
                <MultiSelectCheckboxTag
                  placeholder="Select Level Access Brands"
                  options={brands.map((b) => ({ value: String(b.id), label: b.name }))}
                  value={formStateEdit.selectedBrands.map((b) => String(b.id))}
                  onChange={(ids: string[]) => handleFormEditChange("selectedBrands", ids.map((id: string) => brands.find((b) => String(b.id) === id)).filter(Boolean) as any[])}
                  containerClassName="w-full"
                  showSelectAll
                />
              </div>
            )}

            {formStateEdit.levelAccess === "Organization" && (
              <div className="mb-4">
                <Label className="mb-2 block">Select Level Access Organizations</Label>
                <MultiSelectCheckboxTag
                  placeholder="Select Level Access Organizations"
                  options={organizations.map((o) => ({ value: String(o.id), label: o.name }))}
                  value={formStateEdit.selectedOrganizations.map((o) => String(o.id))}
                  onChange={(ids: string[]) => handleFormEditChange("selectedOrganizations", ids.map((id: string) => organizations.find((o) => String(o.id) === id)).filter(Boolean) as any[])}
                  containerClassName="w-full"
                  showSelectAll
                />
              </div>
            )}

            <div className="mb-4">
              <Label className="mb-2 block">Select Application *</Label>
              <SingleSelect
                options={(applications || []).map((a) => ({
                  label: (a as any).display_name || a.name,
                  value: String(a.id),
                }))}
                defaultValue={
                  formStateEdit.selectedApplication
                    ? String(formStateEdit.selectedApplication.id)
                    : undefined
                }
                onChange={(value) => {
                  const app = applications?.find((a) => String(a.id) === value);
                  handleFormEditChange("selectedApplication", app ?? null);
                  handleFormEditChange("selectedChannel", null);
                  handleFormEditChange("selectedGroupings", null);
                }}
                placeholder="Select Application *"
                containerClassName="w-full"
              />
            </div>

            <div className="mb-4">
              <Label className="mb-2 block">Select Channel *</Label>
              <SingleSelect
                options={channels
                  .filter((ch) =>
                    formStateEdit.selectedApplication
                      ? ch.application_id === formStateEdit.selectedApplication.id
                      : true,
                  )
                  .map((ch) => {
                    const appForChannel = (applications || []).find(
                      (a: any) => String(a.id) === String(ch.application_id),
                    );
                    const appName =
                      (appForChannel as any)?.display_name || appForChannel?.name;
                    const name = ch.name;
                    return {
                      label: appName ? `${name} (${appName})` : name,
                      value: String(ch.id),
                    };
                  })}
                defaultValue={
                  formStateEdit.selectedChannel
                    ? String(formStateEdit.selectedChannel.id)
                    : undefined
                }
                onChange={(value) => {
                  const ch = channels.find((c) => String(c.id) === value);
                  handleFormEditChange("selectedChannel", ch ?? null);
                }}
                placeholder="Select Channel *"
                containerClassName="w-full"
              />
            </div>

            <div className="mb-4">
              <Label className="mb-2 block">Select Grouping (Optional)</Label>
              <SingleSelect
                options={groupings
                  .filter((g) =>
                    formStateEdit.selectedChannel
                      ? g.channel_id === formStateEdit.selectedChannel.id
                      : true,
                  )
                  .map((g) => ({
                    label: `${g.name || "Unnamed"} (${
                      channelMapping[g.channel_id] || "Unknown Channel"
                    })`,
                    value: String(g.id),
                  }))}
                defaultValue={
                  formStateEdit.selectedGroupings
                    ? String(formStateEdit.selectedGroupings.id)
                    : undefined
                }
                onChange={(value) => {
                  const grp = groupings.find((g) => String(g.id) === value);
                  handleFormEditChange("selectedGroupings", grp ?? null);
                }}
                placeholder="Select Grouping (Optional)"
                containerClassName="w-full"
              />
            </div>

            <div className="mb-4">
              <Label className="mb-2 block">Select UI Component *</Label>
              <SingleSelect
                options={component.map((c) => ({
                  label: c.name,
                  value: String(c.id),
                }))}
                defaultValue={formStateEdit.selectedUIComponents ? String(formStateEdit.selectedUIComponents.id) : undefined}
                onChange={(value) => {
                  const comp = component.find((c) => String(c.id) === value);
                  handleFormEditChange("selectedUIComponents", comp ?? null);
                }}
                placeholder="Select UI Component *"
                containerClassName="w-full"
              />
            </div>

            <h2 className="text-lg font-semibold mt-6 mb-1">Display Order</h2>
            <p className="text-sm text-muted-foreground flex items-center mb-4">
              <Info className="h-4 w-4 shrink-0 mr-1" />
              Control the position of this item within the selected {formStateEdit.selectedGroupings ? 'grouping' : 'channel'}. Use "Move to top" to place at the top, or select an item in "Move to below" to place under it. Order is 0 based indexing.
            </p>
            <div className="flex items-center gap-2 mb-4">
              <Label htmlFor="edit-make-top">Move to top</Label>
              <Switch id="edit-make-top" checked={makeItTopEdit} onCheckedChange={setMakeItTopEdit} />
            </div>
            {makeItTopEdit && availableItemsForBelowEdit.length === 0 && (
              <p className="text-sm text-muted-foreground flex items-center mb-4">
                <Info className="h-4 w-4 shrink-0 mr-1" />
                No other items are present in this {formStateEdit.selectedGroupings ? 'grouping' : 'channel'}.
              </p>
            )}
            {!makeItTopEdit && (
              <div className="mb-4">
                <Label className="mb-2 block">Move to below *</Label>
                <SingleSelect
                  options={
                    availableItemsForBelowEdit.length === 0
                      ? []
                      : availableItemsForBelowEdit.map((item: any) => {
                          const fullItemData =
                            drawerItem.find((di: any) => di.id === item.id) || item;
                          let accessInfo = "";
                          let hasBrandAccess = false;
                          if (fullItemData.access?.brand_level?.brands?.length > 0) {
                            accessInfo += `Brand ${
                              fullItemData.access.brand_level.access_mode
                            }: ${fullItemData.access.brand_level.brands
                              .map((b: any) => b.name)
                              .join(", ")}`;
                            hasBrandAccess = true;
                          }
                          if (fullItemData.access?.organization_level?.organizations?.length > 0) {
                            if (hasBrandAccess) accessInfo += " | ";
                            accessInfo += `Org ${
                              fullItemData.access.organization_level.access_mode
                            }: ${fullItemData.access.organization_level.organizations
                              .map((o: any) => o.name)
                              .join(", ")}`;
                          }
                          return {
                            label: accessInfo
                              ? `${item.display_name || item.name} — ${accessInfo}`
                              : item.display_name || item.name,
                            value: item.id,
                          };
                        })
                  }
                  defaultValue={makeItBelowEdit || undefined}
                  onChange={(v) => v && v !== "__none__" && setMakeItBelowEdit(v)}
                  placeholder={
                    availableItemsForBelowEdit.length === 0
                      ? "No other items available"
                      : "Select item"
                  }
                  containerClassName="w-full h-10"
                  disabled={availableItemsForBelowEdit.length === 0}
                />
              </div>
            )}
            <p className="text-sm text-muted-foreground mb-4">
              {makeItTopEdit ? <><strong>Current Position:</strong> &nbsp;<span>Top (Order: 0)</span></> : makeItBelowEdit ? (() => {
                const selectedItemIndex = availableItemsForBelowEdit.findIndex((item: any) => item.id === makeItBelowEdit);
                const selectedItem = availableItemsForBelowEdit.find((item: any) => item.id === makeItBelowEdit);
                const itemName = selectedItem ? (selectedItem.display_name || selectedItem.name) : makeItBelowEdit;
                const newOrder = selectedItemIndex !== -1 ? selectedItemIndex + 1 : (currentData?.display_order || 0);
                return <><strong>Current Position:</strong> &nbsp;<span>Below &quot;{itemName}&quot; (Order: {newOrder})</span></>;
              })() : <><strong>Current Position:</strong> &nbsp;<span>Not Set</span></>}
            </p>

            <Separator className="my-4" />

            <div className="mb-4">
              <Label className="mb-2 block">Select Custom Component Name</Label>
              <MultiSelectCheckboxTag
                placeholder="Select Custom Component Name"
                options={(formStateEdit.selectedUIComponents?.name === "SuperComponent" ? customComponents : customComponents.filter((option: any) => option.name === "All Brand")).map((c: any) => ({ value: String(c.id), label: c.name }))}
                value={formStateEdit.selectedCustomComponents.map((c) => String(c.id))}
                onChange={(ids: string[]) => handleFormEditChange("selectedCustomComponents", ids.map((id: string) => customComponents.find((c: any) => String(c.id) === id)).filter(Boolean) as any[])}
                containerClassName="w-full"
                showSelectAll
              />
            </div>

            <div className="flex items-center gap-2 mt-2 mb-2">
              <Info className="h-4 w-4 shrink-0 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">If you want multiple reports, contact support.</p>
            </div>
            <div
              className={`mb-4 rounded-md ${formStateEdit.selectedUIComponents?.component_type === 2 ? 'pointer-events-none opacity-50 grayscale' : ''}`}
            >
              <Label className="mb-2 block">Select Reports (Optional)</Label>
              <MultiSelectCheckboxTag
                placeholder="Select reports"
                options={reports.map((r: any) => ({ label: r.display_name || String(r.id), value: String(r.id) }))}
                value={(formStateEdit.selectedReports || []).map((r: any) => String(r.id))}
                onChange={(selectedIds: string[]) =>
                  handleFormEditChange(
                    "selectedReports",
                    selectedIds.map((id) => reports.find((r: any) => String(r.id) === id)).filter(Boolean) as any[]
                  )
                }
                containerClassName="w-full"
                showSelectAll
              />
            </div>
            <div
              className={`mb-4 rounded-md ${formStateEdit.selectedUIComponents?.component_type === 2 ? 'pointer-events-none opacity-50 grayscale' : ''}`}
            >
              <Label className="mb-2 block">Select Sandbox</Label>
              <SingleSelect
                options={reportsSandbox.map((s) => ({
                  label: s.display_name,
                  value: String(s.id),
                }))}
                defaultValue={
                  formStateEdit.selectedSandbox
                    ? String(formStateEdit.selectedSandbox.id)
                    : undefined
                }
                onChange={(value) => {
                  const sandbox = reportsSandbox.find((s) => String(s.id) === value);
                  handleFormEditChange("selectedSandbox", sandbox ?? null);
                }}
                placeholder="Select Sandbox"
                containerClassName="w-full"
              />
            </div>

            {formStateEdit.selectedUIComponents?.component_type === 2 && (
              <p className="text-xs text-muted-foreground -mt-2 mb-4">
                Only <strong>Report</strong> and <strong>Tableau</strong> UI components can use Reports/Sandbox options.
              </p>
            )}
            <div className="mb-4">
              <Label className="mb-2 block">Route Endpoint</Label>
              <div className="flex rounded-lg border border-input bg-background overflow-hidden">
                <span className="flex items-center px-3 text-sm text-muted-foreground border-r border-input bg-muted/30">{createEndPoint(formStateEdit)}</span>
                <Input value={formStateEdit.route_endpoint || ""} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFormEditChange("route_endpoint", e.target.value)} className="rounded-none border-0 focus-visible:ring-0" placeholder="my-route" />
              </div>
              <p className="text-xs text-muted-foreground mt-1">Use kebab-case format (e.g., my-route)</p>
            </div>
            <div className="mb-4">
              <Label className="mb-2 block">Icon</Label>
              <IconSelect
                options={iconOptions}
                value={formStateEdit.icon || null}
                onChange={(val) => handleFormEditChange("icon", val)}
                placeholder="Icon"
              />
            </div>
            <div className="flex items-center gap-2 mb-4">
              <Label htmlFor="edit-active">Active:</Label>
              <Switch id="edit-active" checked={formStateEdit.is_active} onCheckedChange={(checked: boolean) => handleFormEditChange("is_active", checked)} />
            </div>
            <div className="flex items-center gap-2 mb-4">
              <Label htmlFor="edit-close-sidebar">Close sidebar after opening:</Label>
              <Switch id="edit-close-sidebar" checked={formStateEdit.closeSidebarOnNavigate} onCheckedChange={(checked: boolean) => handleFormEditChange("closeSidebarOnNavigate", checked)} />
              <span className="text-sm text-muted-foreground ml-1">Collapse the sidebar once this item is opened</span>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <Label htmlFor="edit-set-default">Set as Default:</Label>
              <Switch id="edit-set-default" checked={setAsDefaultEdit} onCheckedChange={setSetAsDefaultEdit} />
              <span className="text-sm text-muted-foreground ml-1">Automatically create default sidebar item</span>
            </div>
            {setAsDefaultEdit && (
              <div className="mb-4 p-3 rounded-lg bg-muted/50 text-sm">
                {(() => {
                  const includedBrands = formStateEdit.brands_exclude?.filter((id: number) => id > 0) || [];
                  const includedOrgs = formStateEdit.organizations_exclude?.filter((id: number) => id > 0) || [];
                  const excludedBrands = formStateEdit.brands_exclude?.filter((id: number) => id < 0) || [];
                  const excludedOrgs = formStateEdit.organizations_exclude?.filter((id: number) => id < 0) || [];
                  if (includedBrands.length > 0 || includedOrgs.length > 0) {
                    const includedEntities = [];
                    if (includedBrands.length > 0) includedEntities.push(`${includedBrands.length} brand(s)`);
                    if (includedOrgs.length > 0) includedEntities.push(`${includedOrgs.length} organization(s)`);
                    return <p className="flex items-center"><Info className="h-4 w-4 shrink-0 mr-2" />This item has INCLUDE access conditions. Default items will be created only for {includedEntities.join(' and ')}.</p>;
                  } else if (excludedBrands.length > 0 || excludedOrgs.length > 0) {
                    const excludedEntities = [];
                    if (excludedBrands.length > 0) excludedEntities.push(`${excludedBrands.length} brand(s)`);
                    if (excludedOrgs.length > 0) excludedEntities.push(`${excludedOrgs.length} organization(s)`);
                    return <p className="flex items-center"><Info className="h-4 w-4 shrink-0 mr-2" />This item has EXCLUDE access conditions for {excludedEntities.join(' and ')}. An application-wide default will be created.</p>;
                  } else {
                    return <p className="flex items-center"><Info className="h-4 w-4 shrink-0 mr-2" />No access conditions detected. An application-wide default item will be created.</p>;
                  }
                })()}
              </div>
            )}
            {formStateEdit.selectedUIComponents?.name !== "SuperComponent" ? (
              <div className="mb-4">
                <Label className="mb-2 block">Params</Label>
                <Input value={formStateEdit.params || ""} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFormEditChange("params", e.target.value)} placeholder="1,2,3" className="w-full" />
                <p className="text-xs text-muted-foreground mt-1">Enter comma-separated numbers (e.g., 1,2,3)</p>
              </div>
            ) : (
              <DynamicNestedForm formStateEdit={formStateEdit} handleFormEditChange={handleFormEditChange} />
            )}
            </div>
            <SheetFooter className="gap-2 border-t pt-4 flex-shrink-0 mt-auto">
              <Button variant="outline" onClick={handleEditDialogClose}>Cancel</Button>
              <Button
                onClick={handleEditSubmit}
                disabled={!formStateEdit.display_name?.trim() || !formStateEdit.selectedApplication || !formStateEdit.selectedChannel || !formStateEdit.selectedUIComponents || (!makeItTopEdit && !makeItBelowEdit) || loadData}
              >
                {loadData ? "Updating..." : "Update Item"}
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
    </>
  );
};

export default Items;
